// Interface labels per language, keys from the copy library (section 7 to 11). Darija is not an interface language.
// Every user-facing string of the customer page, the staff screen, the TV board, the sign-in, setup and dashboard
// screens and the API's error labels lives here (notifications live in templates.ts): components render keys, never
// literals. `npm run check:copy` (scripts/copy-literals.mjs, also run by the apps' tests) fails on a literal.
import type { DeliveryState } from './notify';
import type { Channel, Lang, TicketStatus } from './ticket';

export const LANG_LABELS: Record<Lang, string> = { ar: 'العربية', fr: 'Français', en: 'English' };
export const LANG_DIR: Record<Lang, 'rtl' | 'ltr'> = { ar: 'rtl', fr: 'ltr', en: 'ltr' };
/** The language picker, on every surface: العربية · Français · English, in that order, each label in its own script, never a flag. */
export const LANG_PICKER_ORDER: readonly Lang[] = ['ar', 'fr', 'en'];
/** The product name as written everywhere (logo alt text, page titles): two capitals, no space, Latin script in every language. */
export const BRAND_NAME = 'RingRing';

export interface UiCopy {
  admin_nav: string;
  order_actions: string;
  review_qr_title: string;
  review_qr_heading: string;
  review_qr_hint: string;
  review_qr_open: string;
  qr_download: string;

  // WhatsApp feedback inbox (issue #104).
  feedback_nav: string;
  feedback_heading: string;
  feedback_intro: string;
  feedback_received: string;
  feedback_excellent_share: string;
  feedback_private_notes: string;
  feedback_unread: string;
  feedback_all: string;
  feedback_pending: string;
  feedback_period: string;
  feedback_period_all: string;
  feedback_period_7: string;
  feedback_period_30: string;
  feedback_period_90: string;
  feedback_rating_filter: string;
  feedback_rating_all: string;
  feedback_search: string;
  feedback_mix: string;
  feedback_mix_hint: string;
  feedback_no_rating: string;
  feedback_no_note: string;
  feedback_waiting: string;
  feedback_empty_title: string;
  feedback_empty_body: string;
  feedback_empty_filter: string;
  feedback_mark_read: string;
  feedback_read: string;
  feedback_refresh: string;
  feedback_loading: string;
  feedback_order: string;
  feedback_results: string;
  feedback_page: string;
  feedback_previous: string;
  feedback_next: string;
  feedback_reset: string;
  feedback_newest: string;
  feedback_google: string;
  feedback_google_hint: string;

  // Shared visual system: navigation, service overview and guest experience.
  design_restaurant_space: string;
  design_team_space: string;
  design_auth_heading: string;
  design_auth_accent: string;
  design_auth_body: string;
  design_auth_footer: string;
  design_preview: string;
  design_secure_access: string;
  design_workspace: string;
  design_sidebar_note: string;
  design_hospitality: string;
  design_welcome: string;
  design_service_heading: string;
  design_service_intro: string;
  design_live_service: string;
  design_staff_intro: string;
  design_open_orders: string;
  design_all_orders: string;
  design_search: string;
  design_no_results: string;
  design_empty_hint: string;
  design_connected: string;
  design_connecting: string;
  design_active_counters: string;
  design_team_devices: string;
  design_guest_languages: string;
  design_counter_tools: string;
  design_counter_intro: string;
  design_pin_heading: string;
  design_pin_intro: string;
  design_guest_heading: string;
  design_guest_footer: string;
  design_board_waiting: string;

  order_preparing: string;
  order_ready: string;
  order_collected: string;
  order_cancelled: string;
  order_expired: string;
  order_abandoned: string;
  message_queued: string;
  message_sending: string;
  message_sent: string;
  message_delivered: string;
  message_failed: string;
  retry: string;
  edit_phone: string;
  mark_ready: string;
  mark_collected: string;
  ready_confirmation: string; // {{order_number}}
  undo: string;
  empty: string;
  orders: string;
  language: string;
  tv_heading: string;
  tv_sub: string;
  offline: string;
  needs_call: string;
  new_number: string;
  add_receipt: string;
  /** The staff screen's receipt field in ticket mode (issue #59); the diner's field keeps `receipt_prompt`. */
  receipt_no_label: string;
  resend: string;
  cancel_order: string;
  pin_prompt: string;
  sign_in: string;
  get_alert: string;
  whatsapp_cta: string;
  keep_open: string;
  alert_set: string;
  alert_set_body: string;
  your_number: string;
  receipt_prompt: string;
  continue: string;
  closed: string;
  paused_listen: string;
  link_expired: string;
  link_invalid: string;
  help: string;
  privacy_link: string;
  privacy_body: string;
  no_phone_alt: string;
  ready_sr: string; // {{order_number}}
  pin_wrong: string; // {{attempts_left}}
  pin_locked: string; // {{minutes}}
  pp_not_found: string;
  // Customer page states (body line under the status label).
  pickup_at: string; // {{pickup_point}}
  ready_body: string;
  collected_body: string;
  cancelled_body: string;
  expired_body: string;
  abandoned_body: string;
  // Sound unlock control: browsers only let a page play audio after a tap.
  enable_sound: string;
  sound_on: string;
  sound_hint: string;
  // Registration errors shown on the customer page.
  number_open: string;
  bad_number: string;
  too_many_open: string;
  // Channel labels on the staff screen.
  channel_whatsapp: string;
  channel_web: string;
  channel_sms: string;
  channel_push: string;
  channel_none: string;
  order_n: string; // {{order_number}}
  other_orders: string;
  your_orders: string;
  another_order: string;
  device_limit: string; // {{max}}
  no_access: string;
  signin_title: string;
  signin_intro: string;
  phone_prompt: string;
  phone_hint: string;
  send_code: string;
  code_prompt: string;
  code_sent: string; // {{phone}}
  code_expired: string;
  code_not_sent: string;
  code_cooldown: string; // {{seconds}}
  bad_phone: string;
  resend_code: string;
  resend_in: string; // {{seconds}}
  change_phone: string;
  sign_out: string;
  sign_out_all: string;
  signed_in_as: string;
  your_venues: string;
  no_venue_yet: string;
  role_owner: string;
  role_manager: string;
  staff_screen: string;
  tv_board: string;
  // Staff screen rows and header (issue #14).
  minutes_waiting: string; // {{minutes}}
  resend_countdown: string; // {{seconds}}
  resend_exhausted: string;
  // WhatsApp call to the guest (issue #95).
  call_whatsapp: string;
  call_auto_in: string; // {{time}}
  call_after_ready: string; // {{minutes}}
  call_calling: string;
  call_answered: string;
  call_not_answered: string;
  call_failed: string;
  call_no_permission: string;
  // POC demo venue (issue #95).
  demo_reset: string;
  demo_reset_question: string;
  demo_reset_phone: string;
  demo_reset_confirm: string;
  demo_reset_keep: string;
  demo_reset_done: string;
  signin_methods: string;
  signin_email: string;
  signin_phone: string;
  signin_email_intro: string;
  signin_identifier: string;
  signin_secret: string;
  signin_busy: string;
  signin_unavailable: string;
  cancel_confirm: string; // {{order_number}}
  confirm_cancel: string;
  keep_order: string;
  sms_used: string; // {{used}} {{quota}}
  sms_quota_warning: string;
  sms_quota_exhausted: string;
  // TV board: the one-time start overlay (browsers only play sound after a tap), the sound state and the new-number badge.
  tv_start: string;
  tv_start_hint: string;
  tv_sound_off: string;
  tv_new: string;
  // Correcting a wrong number on the staff screen (issue #17) and the notice on the diner's page.
  edit_number: string;
  edit_number_prompt: string; // {{order_number}}
  save_number: string;
  edit_number_cancel: string;
  number_locked: string;
  number_corrected: string; // {{order_number}}
  // Home, the five-step setup and the dashboard shell (issue #10).
  home_tagline: string;
  open_dashboard: string;
  setup_title: string;
  setup_intro: string;
  add_venue_title: string;
  step_of: string; // {{n}} {{total}}
  step_organization: string;
  step_venue: string;
  step_hours: string;
  step_mode: string;
  step_languages: string;
  org_name: string;
  org_name_hint: string;
  venue_name: string;
  venue_name_hint: string;
  city: string;
  city_hint: string;
  hours_intro: string;
  open_day: string;
  closed_day: string;
  opens_at: string;
  closes_at: string;
  mode_prompt: string;
  mode_scan: string;
  mode_scan_desc: string;
  mode_ticket: string;
  mode_ticket_desc: string;
  languages_prompt: string;
  languages_hint: string;
  default_lang: string;
  back: string;
  next: string;
  finish: string;
  saving: string;
  bad_org_name: string;
  bad_venue_name: string;
  bad_city: string;
  bad_hours: string;
  bad_languages: string;
  dashboard: string;
  venue_switcher: string;
  venue_details: string;
  mode_label: string;
  languages_label: string;
  default_lang_label: string;
  hours_label: string;
  pickup_points: string;
  pickup_points_intro: string;
  add_pickup_point: string;
  pickup_point_name: string;
  pickup_point_default: string;
  customer_url: string;
  open_link: string;
  copy_link: string;
  copied: string;
  rename: string;
  save: string;
  cancel: string;
  archive: string;
  unarchive: string;
  archived: string;
  archived_points: string;
  last_pickup_point: string;
  add_venue: string;
  account: string;
  day_mon: string;
  day_tue: string;
  day_wed: string;
  day_thu: string;
  day_fri: string;
  day_sat: string;
  day_sun: string;
  // Staff screen: the kitchen view toggle and the pickup point filter (issue #18).
  kitchen_view: string;
  pickup_point_filter: string;
  all_pickup_points: string;
  open_orders: string; // {{count}}
  // Voice call-out (issue #19): what the voice says, and the voice state next to the sound tag.
  tv_callout: string; // {{order_number}} in words
  tv_voice_on: string; // {{langs}}
  tv_voice_off: string;
  // Pickup point setting on the account page: the optional second language of the call-out.
  voice_lang_2: string;
  voice_lang_none: string;
  default_lang_is: string; // {{lang}}
  saved: string;
  // Sign-in code by e-mail (issue #23): the backup channel on the sign-in page and the backup address on the account page.
  send_by_email: string;
  send_by_whatsapp: string;
  code_sent_email: string; // {{email}}
  code_prompt_email: string;
  email_hint: string;
  no_backup_email: string;
  backup_email: string;
  backup_email_intro: string;
  backup_email_none: string;
  email_prompt: string;
  send_email_code: string;
  email_code_sent: string; // {{email}}
  confirm_email: string;
  email_confirmed: string;
  email_removed: string;
  remove_email: string;
  change_email: string;
  bad_email: string;
  // Staff PIN and devices on the dashboard, and the staff screen's "signed out" notice (issue #12).
  staff_pin_section: string;
  staff_pin_intro: string;
  no_staff_pin: string;
  staff_pin_set: string;
  staff_pin_set_on: string; // {{date}}
  generate_pin: string;
  regenerate_pin: string;
  regenerate_warning: string; // {{count}}
  confirm_regenerate: string;
  new_pin_title: string;
  new_pin_note: string;
  pin_noted: string;
  devices: string;
  devices_intro: string;
  no_devices: string;
  former_devices: string;
  revoke: string;
  device_active: string;
  device_revoked: string;
  device_signed_out_pin: string;
  device_replaced: string;
  device_expired: string;
  first_seen: string;
  last_seen: string;
  seen_just_now: string;
  seen_minutes_ago: string; // {{minutes}}
  seen_hours_ago: string; // {{hours}}
  seen_days_ago: string; // {{days}}
  unknown_device: string;
  signed_in_from: string; // {{pickup_point}}
  device_revoked_message: string;
  // Signed TV board link (issue #16, US-43): the link block under each counter on the dashboard, and what the board
  // shows when it was opened without a key or with one that no longer matches.
  board_link: string;
  board_link_intro: string;
  copy_board_link: string;
  revoke_board_link: string;
  revoke_board_link_warning: string;
  confirm_revoke_board_link: string;
  board_link_renewed: string;
  tv_link_missing_title: string;
  tv_link_missing_body: string;
  tv_link_invalid_title: string;
  tv_link_invalid_body: string;
  // Venue settings (issue #11): the settings page, the closed page outside the hours, the paused tag on the staff screen.
  settings: string;
  settings_title: string;
  settings_intro: string;
  back_to_dashboard: string;
  settings_saved: string;
  registrations_label: string;
  pause_label: string;
  pause_hint: string;
  paused_tag: string;
  active_tag: string;
  feedback_section: string;
  place_id_label: string;
  place_id_hint: string;
  review_link_preview: string;
  feedback_label: string;
  feedback_hint: string;
  google_link_label: string;
  google_link_hint: string;
  google_link_needs_id: string;
  on: string;
  off: string;
  add_range: string;
  remove_range: string;
  today_hours: string;
  closed_today: string;
  bad_mode: string;
  bad_default_lang: string;
  bad_place_id: string;
  bad_setting: string;
  // API error labels (issue #21): what a staff or owner screen shows when a request is refused, by error code
  // (`apiErrorLabel`). Codes with a customer-page equivalent reuse that key; these are the ones without.
  request_failed: string;
  error_unauthorized: string;
  error_not_found: string;
  error_bad_request: string;
  error_not_waiting: string;
  error_not_open: string;
  error_not_ready: string;
  error_paused: string;
  error_resend_cooldown: string;
  error_not_callable: string;
  error_call_in_progress: string;
  error_demo_login: string;
  error_wrong_code: string;
  error_locked: string;
  error_code_cooldown: string;
  error_bad_role: string;
  error_already_member: string;
  error_last_owner: string;
  error_bad_mode: string;
  error_bad_name: string;
  error_bad_lang: string;
  error_no_free_number: string;
  error_session_expired: string;
  // API labels of the logo and message refusals (issue #20), without the variables the settings page fills in.
  error_message_too_long: string;
  error_message_placeholder: string;
  error_logo_too_large: string;
  error_logo_dimensions: string;
  // Venue logo and the venue's own message texts (issue #20, US-5). Darija is not in release 1.
  logo_section: string;
  logo_intro: string;
  logo_hint: string; // {{max}}
  logo_current: string;
  logo_none: string;
  logo_choose: string;
  logo_replace: string;
  logo_remove: string;
  logo_uploading: string;
  logo_saved: string;
  logo_removed: string;
  logo_not_saved: string;
  logo_empty: string;
  logo_too_large: string; // {{max}}
  logo_bad_type: string;
  logo_dimensions: string; // {{px}}
  logo_unsafe: string;
  messages_section: string;
  messages_intro: string;
  messages_count_hint: string; // {{max}}
  message_lang: string;
  message_registered: string;
  message_registered_hint: string;
  message_ready: string;
  message_ready_hint: string;
  message_closing: string;
  message_counter: string; // {{count}}, {{max}}
  message_over: string; // {{count}}
  message_required: string;
  message_insert: string;
  placeholder_order_number: string;
  placeholder_venue_name: string;
  placeholder_pickup_point: string;
  message_preview: string;
  message_sms_preview: string;
  message_sms_default: string;
  message_restore: string;
  message_default_tag: string;
  message_custom_tag: string;
  message_problem: string; // {{lang}}, {{message}}, {{problem}}
  message_too_long: string; // {{max}}
  message_missing_placeholder: string; // {{placeholder}}
  message_bad_placeholder: string; // {{placeholder}}
  message_link: string;
  // The counter poster (issue #13): its card on the settings page and the A4 / A5 downloads per pickup point.
  poster_section: string;
  poster_intro: string;
  poster_lang_2: string;
  poster_lang_hint: string; // {{lang}}
  poster_one_lang: string;
  poster_download: string; // {{size}}
  poster_downloading: string;
  poster_downloaded: string;
  poster_failed: string;
  poster_save_first: string;
  bad_poster_lang: string;
  error_bad_poster_size: string;
  error_pickup_point_archived: string;
}

export const UI: Record<Lang, UiCopy> = {
  fr: {
    admin_nav: 'Administration',
    order_actions: 'Actions de la commande',
    review_qr_title: "QR code Google",
    review_qr_heading: "Un scan pour partager un avis.",
    review_qr_hint: "Présentez ce code à vos clients ou téléchargez-le pour l’afficher au comptoir. Il ouvre la page d’avis Google de votre établissement.",
    review_qr_open: "Ouvrir le lien Google",
    qr_download: "Télécharger le QR code (SVG)",
    feedback_nav: "Avis clients",
    feedback_heading: "À l’écoute de vos clients.",
    feedback_intro: "Retrouvez les évaluations et les messages privés reçus sur WhatsApp.",
    feedback_received: "Avis reçus",
    feedback_excellent_share: "Évaluations « Excellent »",
    feedback_private_notes: "Messages privés",
    feedback_unread: "Non lus",
    feedback_all: "Tous les avis",
    feedback_pending: "Sans réponse",
    feedback_period: "Période",
    feedback_period_all: "Tout l’historique",
    feedback_period_7: "7 derniers jours",
    feedback_period_30: "30 derniers jours",
    feedback_period_90: "90 derniers jours",
    feedback_rating_filter: "Évaluation",
    feedback_rating_all: "Toutes les évaluations",
    feedback_search: "Rechercher un numéro, un message ou un comptoir",
    feedback_mix: "La voix de vos clients",
    feedback_mix_hint: "Répartition des évaluations sur la période sélectionnée.",
    feedback_no_rating: "Sans évaluation",
    feedback_no_note: "Le client a laissé une évaluation, sans message.",
    feedback_waiting: "Aucune réponse reçue à cette question.",
    feedback_empty_title: "Les avis arrivent ici.",
    feedback_empty_body: "Les réponses et messages WhatsApp de vos clients s’afficheront dans cet espace.",
    feedback_empty_filter: "Aucun avis ne correspond à ces filtres.",
    feedback_mark_read: "Marquer comme lu",
    feedback_read: "Lu",
    feedback_refresh: "Actualiser",
    feedback_loading: "Chargement des avis…",
    feedback_order: "Commande n° {{number}}",
    feedback_results: "{{count}} résultat(s)",
    feedback_page: "Page {{page}} sur {{pages}}",
    feedback_previous: "Précédent",
    feedback_next: "Suivant",
    feedback_reset: "Effacer les filtres",
    feedback_newest: "Plus récents en premier",
    feedback_google: "Avis Google",
    feedback_google_hint: "Les avis publiés sur Google sont consultables sur votre fiche Google.",
    design_restaurant_space: "ESPACE RESTAURANT",
    design_team_space: "ESPACE ÉQUIPE",
    design_auth_heading: "Le service avance.",
    design_auth_accent: "L’esprit est libre.",
    design_auth_body: "Du comptoir au retrait, chaque commande trouve son rythme. Votre équipe garde le fil, vos clients profitent du moment.",
    design_auth_footer: "Pensé pour le comptoir. Conçu pour votre équipe.",
    design_preview: "APERÇU DU PARCOURS CLIENT",
    design_secure_access: "Un accès personnel à votre espace restaurant.",
    design_workspace: "Votre espace",
    design_sidebar_note: "Un numéro. Une alerte. À table.",
    design_hospitality: "Au rythme de votre service",
    design_welcome: "Ravi de vous retrouver.",
    design_service_heading: "Votre service, en un coup d’œil.",
    design_service_intro: "Vos comptoirs, votre équipe et vos écrans réunis au même endroit.",
    design_live_service: "Service en cours",
    design_staff_intro: "Chaque commande au bon moment.",
    design_open_orders: "Commandes en cours",
    design_all_orders: "Toutes",
    design_search: "Rechercher un numéro",
    design_no_results: "Aucune commande ne correspond à votre recherche.",
    design_empty_hint: "Les nouvelles commandes apparaîtront ici. Vous pouvez aussi créer un numéro au comptoir.",
    design_connected: "En direct",
    design_connecting: "Connexion en cours",
    design_active_counters: "Comptoirs actifs",
    design_team_devices: "Appareils connectés",
    design_guest_languages: "Langues proposées",
    design_counter_tools: "Liens, affiches et configuration",
    design_counter_intro: "Prêt pour le prochain service.",
    design_pin_heading: "Bienvenue au comptoir.",
    design_pin_intro: "Saisissez le code à 6 chiffres fourni par votre responsable pour retrouver les commandes.",
    design_guest_heading: "Vous êtes au bon endroit.",
    design_guest_footer: "Le plaisir commence ici.",
    design_board_waiting: "Votre commande se prépare.",
    order_preparing: 'En préparation',
    order_ready: 'Prête à être retirée',
    order_collected: 'Retirée',
    order_cancelled: 'Annulée',
    order_expired: 'Non retirée',
    order_abandoned: 'Clôturée',
    message_queued: "En attente d'envoi",
    message_sending: 'Envoi en cours',
    message_sent: 'Envoyé',
    message_delivered: 'Distribué',
    message_failed: "Échec de l'envoi",
    retry: 'Réessayer',
    edit_phone: 'Vérifier le numéro',
    mark_ready: 'Marquer comme prête',
    mark_collected: 'Marquer comme retirée',
    ready_confirmation: 'Commande n° {{order_number}} marquée comme prête.',
    undo: 'Annuler cette action',
    empty: 'Aucune commande pour le moment.',
    orders: 'Commandes',
    language: 'Langue',
    tv_heading: 'Commandes prêtes',
    tv_sub: 'Retrait au comptoir',
    offline: 'Connexion interrompue. Les informations affichées peuvent ne pas être à jour.',
    needs_call: 'À appeler',
    new_number: 'Nouveau numéro',
    add_receipt: 'Ajouter un numéro de ticket',
    receipt_no_label: 'Numéro du ticket',
    resend: 'Renvoyer',
    cancel_order: 'Annuler la commande',
    pin_prompt: 'Code du comptoir',
    sign_in: 'Se connecter',
    get_alert: 'Recevoir mon alerte',
    whatsapp_cta: 'Me prévenir sur WhatsApp',
    keep_open: 'Pas de WhatsApp ? Gardez cette page ouverte : elle sonnera quand votre commande sera prête.',
    alert_set: 'Votre alerte est activée.',
    alert_set_body: 'Nous vous enverrons un message lorsque votre commande sera prête.',
    your_number: 'Votre numéro de commande',
    receipt_prompt: 'Indiquez le numéro inscrit sur votre ticket',
    continue: 'Continuer',
    closed: 'Ce comptoir est fermé pour le moment.',
    paused_listen: "Les enregistrements sont suspendus pour le moment. Écoutez votre numéro : l'équipe l'annoncera.",
    link_expired: 'Ce lien a expiré. Scannez de nouveau le QR code au comptoir.',
    link_invalid: "Ce lien n'est pas valide. Scannez le QR code au comptoir pour obtenir votre numéro.",
    help: "Besoin d'aide ? Adressez-vous à l'équipe.",
    privacy_link: 'Comment votre numéro est utilisé',
    privacy_body:
      'Votre numéro sert uniquement aux messages de service liés à cette commande. Il est supprimé 30 jours après la clôture. Envoyez STOP pour ne plus recevoir de messages.',
    no_phone_alt: 'Vous pouvez aussi attendre sur place.',
    ready_sr: 'Commande n° {{order_number}} prête à être retirée.',
    pin_wrong: 'Code incorrect. Tentatives restantes : {{attempts_left}}.',
    pin_locked: 'Trop de tentatives. Réessayez dans {{minutes}} min.',
    pp_not_found: 'Comptoir introuvable. Vérifiez le lien ou saisissez le code de cet établissement.',
    pickup_at: 'Retrait : {{pickup_point}}',
    ready_body: 'Présentez ce numéro au comptoir.',
    collected_body: 'Votre commande a été retirée. Bon appétit !',
    cancelled_body: "Cette commande a été annulée. Pour toute question, adressez-vous à l'équipe.",
    expired_body: "Cette commande n'a pas été retirée à temps. Adressez-vous à l'équipe au comptoir.",
    abandoned_body: "Cette commande n'est plus suivie. Adressez-vous à l'équipe.",
    enable_sound: 'Activer le son',
    sound_on: 'Son activé',
    sound_hint: 'Appuyez une fois pour que cette page puisse sonner.',
    number_open: 'Ce numéro est déjà enregistré. Vérifiez votre ticket.',
    bad_number: 'Indiquez un numéro de 1 à 6 chiffres.',
    too_many_open: "Vous avez déjà plusieurs commandes en cours sur cet appareil. Adressez-vous à l'équipe.",
    channel_whatsapp: 'WhatsApp',
    channel_web: 'Page ouverte',
    channel_sms: 'SMS',
    channel_push: 'Notification',
    channel_none: 'Sans alerte',
    order_n: 'Commande n° {{order_number}}',
    other_orders: 'Vos autres commandes',
    your_orders: 'Vos commandes en cours',
    another_order: 'Ajouter une autre commande',
    device_limit: "Vous avez déjà {{max}} commandes en cours sur cet appareil. Retirez-en une avant d'en ajouter une nouvelle.",
    no_access: 'Vous n’avez pas accès à cet établissement.',
    signin_title: 'Connexion',
    signin_intro: 'Recevez un code de connexion sur WhatsApp.',
    phone_prompt: 'Votre numéro de téléphone',
    phone_hint: 'Exemple : 06 12 34 56 78',
    send_code: 'Recevoir mon code',
    code_prompt: 'Code reçu sur WhatsApp',
    code_sent: 'Code envoyé sur WhatsApp au {{phone}}. Il est valable 10 minutes.',
    code_expired: 'Ce code a expiré. Demandez-en un nouveau.',
    code_not_sent: 'Le code n’a pas pu être envoyé. Réessayez dans un instant.',
    code_cooldown: 'Un code vient d’être envoyé. Patientez {{seconds}} s avant d’en demander un autre.',
    bad_phone: 'Numéro invalide. Exemple : 06 12 34 56 78',
    resend_code: 'Renvoyer le code',
    resend_in: 'Renvoyer dans {{seconds}} s',
    change_phone: 'Changer de numéro',
    sign_out: 'Se déconnecter',
    sign_out_all: 'Déconnecter tous les appareils',
    signed_in_as: 'Connecté avec le numéro',
    your_venues: 'Vos établissements',
    no_venue_yet: 'Aucun établissement pour le moment.',
    role_owner: 'Propriétaire',
    role_manager: 'Gérant',
    staff_screen: 'Écran équipe',
    tv_board: 'Écran TV',
    minutes_waiting: '{{minutes}} min',
    resend_countdown: 'Renvoyer ({{seconds}} s)',
    resend_exhausted: 'Limite de renvois atteinte',
    call_whatsapp: 'Appeler sur WhatsApp',
    call_auto_in: 'Appel WhatsApp automatique dans {{time}}',
    call_after_ready: 'Appel WhatsApp automatique {{minutes}} min après « Prête »',
    call_calling: 'Appel WhatsApp en cours…',
    call_answered: 'Appel WhatsApp : répondu',
    call_not_answered: 'Appel WhatsApp : pas de réponse',
    call_failed: 'Appel WhatsApp impossible',
    call_no_permission: "Appel WhatsApp : le client ne l'a pas autorisé",
    demo_reset: 'Réinitialiser la démo',
    demo_reset_question: 'Remplacer toutes les commandes par les commandes de démonstration ?',
    demo_reset_phone: 'Votre numéro WhatsApp pour les commandes 1 et 6 (facultatif)',
    demo_reset_confirm: 'Réinitialiser',
    demo_reset_keep: 'Annuler',
    demo_reset_done: 'Démo réinitialisée.',
    signin_methods: 'Mode de connexion',
    signin_email: 'E-mail',
    signin_phone: 'Téléphone',
    signin_email_intro: 'Retrouvez votre espace et votre service en cours.',
    signin_identifier: 'E-mail ou identifiant',
    signin_secret: 'Mot de passe',
    signin_busy: 'Connexion…',
    signin_unavailable: 'La connexion avec un mot de passe n’est pas disponible. Utilisez l’onglet Téléphone.',
    cancel_confirm: 'Annuler la commande n° {{order_number}} ?',
    confirm_cancel: 'Oui, annuler',
    keep_order: 'Non, garder',
    sms_used: 'SMS utilisés : {{used}} / {{quota}}',
    sms_quota_warning: 'bientôt épuisé',
    sms_quota_exhausted: 'quota atteint',
    tv_start: "Appuyez pour démarrer l'affichage",
    tv_start_hint: 'Une seule fois : cela active le carillon et les annonces vocales sur cet écran.',
    tv_sound_off: "Son coupé. Appuyez sur OK ou touchez l'écran pour l'activer.",
    tv_new: 'Nouveau',
    edit_number: 'Corriger le numéro',
    edit_number_prompt: 'Nouveau numéro pour la commande n° {{order_number}}',
    save_number: 'Enregistrer le numéro',
    edit_number_cancel: 'Ne rien changer',
    number_locked: 'La commande est prête : le numéro ne peut plus être corrigé.',
    number_corrected: 'Votre numéro de commande a été corrigé : {{order_number}}.',
    home_tagline: 'Le bipeur virtuel des restaurants : vos clients sont prévenus sur WhatsApp quand leur commande est prête.',
    open_dashboard: 'Ouvrir le tableau de bord',
    setup_title: 'Configurer votre établissement',
    setup_intro: 'Cinq étapes, moins de dix minutes. Vous pourrez tout modifier ensuite.',
    add_venue_title: 'Ajouter un établissement',
    step_of: 'Étape {{n}} sur {{total}}',
    step_organization: 'Organisation',
    step_venue: 'Établissement',
    step_hours: 'Horaires',
    step_mode: 'Numérotation',
    step_languages: 'Langues',
    org_name: 'Nom de votre organisation',
    org_name_hint: 'L’enseigne ou la société : le nom qui regroupe vos établissements.',
    venue_name: 'Nom de l’établissement',
    venue_name_hint: 'Tel qu’il apparaîtra dans les messages envoyés aux clients.',
    city: 'Ville',
    city_hint: 'Exemple : Casablanca',
    hours_intro: 'Les heures d’ouverture du comptoir. Une fermeture après minuit (par exemple 18:00 à 01:00) est acceptée.',
    open_day: 'Ouvert',
    closed_day: 'Fermé',
    opens_at: 'Ouverture',
    closes_at: 'Fermeture',
    mode_prompt: 'Comment vos clients obtiennent-ils leur numéro ?',
    mode_scan: 'Scan',
    mode_scan_desc: 'Le client scanne le QR code et reçoit le numéro suivant à l’écran. Le caissier l’écrit sur le ticket.',
    mode_ticket: 'Ticket',
    mode_ticket_desc: 'Votre caisse imprime déjà un numéro : le client le saisit après avoir scanné le QR code.',
    languages_prompt: 'Langues de la page client',
    languages_hint: 'Choisissez au moins une langue. La langue par défaut s’affiche au premier chargement.',
    default_lang: 'Langue par défaut',
    back: 'Retour',
    next: 'Continuer',
    finish: 'Terminer',
    saving: 'Enregistrement…',
    bad_org_name: 'Indiquez le nom de votre organisation (80 caractères au plus).',
    bad_venue_name: 'Indiquez le nom de l’établissement (80 caractères au plus).',
    bad_city: 'Indiquez la ville.',
    bad_hours: 'Vérifiez les horaires : au moins un jour ouvert, et une heure de fermeture différente de l’heure d’ouverture.',
    bad_languages: 'Choisissez au moins une langue.',
    dashboard: 'Tableau de bord',
    venue_switcher: 'Établissement affiché',
    venue_details: 'Établissement',
    mode_label: 'Numérotation',
    languages_label: 'Langues',
    default_lang_label: 'Langue par défaut',
    hours_label: 'Horaires',
    pickup_points: 'Comptoirs',
    pickup_points_intro: 'Chaque comptoir a sa propre numérotation, son QR code et son écran TV.',
    add_pickup_point: 'Ajouter un comptoir',
    pickup_point_name: 'Nom du comptoir',
    pickup_point_default: 'Comptoir 1',
    customer_url: 'Lien du QR code',
    open_link: 'Ouvrir',
    copy_link: 'Copier le lien',
    copied: 'Lien copié',
    rename: 'Renommer',
    save: 'Enregistrer',
    cancel: 'Annuler',
    archive: 'Archiver',
    unarchive: 'Réactiver',
    archived: 'Archivé',
    archived_points: 'Comptoirs archivés',
    last_pickup_point: 'Impossible d’archiver le dernier comptoir actif.',
    add_venue: 'Ajouter un établissement',
    account: 'Compte',
    day_mon: 'Lundi',
    day_tue: 'Mardi',
    day_wed: 'Mercredi',
    day_thu: 'Jeudi',
    day_fri: 'Vendredi',
    day_sat: 'Samedi',
    day_sun: 'Dimanche',
    kitchen_view: 'Vue cuisine',
    pickup_point_filter: 'Comptoir affiché',
    all_pickup_points: 'Tous les comptoirs',
    open_orders: '{{count}} en cours',
    tv_callout: 'Numéro {{order_number}}',
    tv_voice_on: 'Voix : {{langs}}',
    tv_voice_off: 'Aucune voix sur cet écran : carillon seul',
    voice_lang_2: 'Deuxième langue des annonces vocales',
    voice_lang_none: 'Aucune',
    default_lang_is: 'Langue par défaut : {{lang}}',
    saved: 'Enregistré',
    send_by_email: 'Recevoir le code par e-mail',
    send_by_whatsapp: 'Recevoir le code sur WhatsApp',
    code_sent_email: 'Code envoyé par e-mail à {{email}}. Il est valable 10 minutes.',
    code_prompt_email: 'Code reçu par e-mail',
    email_hint: 'Vérifiez aussi le dossier spam.',
    no_backup_email: 'Aucun e-mail de secours pour ce numéro. Connectez-vous avec WhatsApp, puis ajoutez un e-mail de secours dans votre compte.',
    backup_email: 'E-mail de secours',
    backup_email_intro: 'Pour recevoir votre code de connexion par e-mail si WhatsApp n’est pas disponible.',
    backup_email_none: 'Aucun e-mail de secours.',
    email_prompt: 'Votre adresse e-mail',
    send_email_code: 'Envoyer un code de confirmation',
    email_code_sent: 'Code envoyé à {{email}}. Saisissez-le pour confirmer cette adresse.',
    confirm_email: 'Confirmer l’adresse',
    email_confirmed: 'E-mail de secours enregistré.',
    email_removed: 'E-mail de secours supprimé.',
    remove_email: 'Supprimer l’e-mail de secours',
    change_email: 'Changer d’adresse',
    bad_email: 'Adresse e-mail invalide. Exemple : nom@exemple.ma',
    staff_pin_section: 'Code du comptoir',
    staff_pin_intro: 'Votre équipe saisit ce code à 6 chiffres une fois par appareil pour ouvrir l’écran du comptoir. Chaque appareil reste connecté 30 jours.',
    no_staff_pin: 'Aucun code défini.',
    staff_pin_set: 'Code défini.',
    staff_pin_set_on: 'Code défini le {{date}}.',
    generate_pin: 'Générer un code',
    regenerate_pin: 'Régénérer le code',
    regenerate_warning: 'Régénérer le code déconnecte {{count}} appareil(s). L’ancien code cesse de fonctionner.',
    confirm_regenerate: 'Oui, régénérer',
    new_pin_title: 'Nouveau code du comptoir',
    new_pin_note: 'Notez-le maintenant : il ne sera plus affiché.',
    pin_noted: 'J’ai noté le code',
    devices: 'Appareils connectés',
    devices_intro: 'Chaque appareil qui a saisi le code. Révoquer un appareil le ramène au formulaire de code en moins d’une minute.',
    no_devices: 'Aucun appareil connecté pour le moment.',
    former_devices: 'Anciens appareils',
    revoke: 'Révoquer',
    device_active: 'Connecté',
    device_revoked: 'Révoqué',
    device_signed_out_pin: 'Déconnecté par le nouveau code',
    device_replaced: 'Remplacé par une nouvelle connexion',
    device_expired: 'Expiré',
    first_seen: 'Première connexion',
    last_seen: 'Dernière activité',
    seen_just_now: 'à l’instant',
    seen_minutes_ago: 'il y a {{minutes}} min',
    seen_hours_ago: 'il y a {{hours}} h',
    seen_days_ago: 'il y a {{days}} j',
    unknown_device: 'Appareil inconnu',
    signed_in_from: 'Connecté depuis {{pickup_point}}',
    device_revoked_message: 'Cet appareil a été déconnecté. Saisissez le code du comptoir pour continuer.',
    board_link: "Lien de l'écran TV",
    board_link_intro: "Ouvrez ce lien sur la télévision du comptoir : il affiche les numéros sans code et ne permet aucune action. Toute personne qui a ce lien voit l'écran ; révoquez-le s'il a circulé.",
    copy_board_link: "Copier le lien de l'écran TV",
    revoke_board_link: 'Révoquer ce lien',
    revoke_board_link_warning: "L'écran qui utilise ce lien s'arrête dans la minute. Un nouveau lien s'affiche ici, à ouvrir sur la télévision.",
    confirm_revoke_board_link: 'Oui, révoquer',
    board_link_renewed: "Ancien lien révoqué. Voici le nouveau : ouvrez-le sur la télévision.",
    tv_link_missing_title: "Lien d'affichage requis",
    tv_link_missing_body: "Cet écran s'ouvre uniquement avec le lien de l'écran TV. Copiez-le depuis le tableau de bord du gérant, sous le comptoir.",
    tv_link_invalid_title: "Lien d'affichage non valide",
    tv_link_invalid_body: 'Ce lien a peut-être été révoqué. Ouvrez le lien actuel depuis le tableau de bord du gérant, sous le comptoir.',
    settings: 'Paramètres',
    settings_title: 'Paramètres de l’établissement',
    settings_intro: 'Les modifications s’appliquent immédiatement à la page client, à l’écran équipe et à l’écran TV. Les commandes en cours sont conservées.',
    back_to_dashboard: 'Retour au tableau de bord',
    settings_saved: 'Paramètres enregistrés',
    registrations_label: 'Enregistrements par QR code',
    pause_label: 'Suspendre les enregistrements',
    pause_hint: 'Pendant un rush ou une panne : le QR code ne délivre plus de numéro et la page client demande d’écouter son numéro. L’écran équipe et l’écran TV continuent de fonctionner.',
    paused_tag: 'Enregistrements suspendus',
    active_tag: 'Enregistrements actifs',
    feedback_section: 'Avis des clients',
    place_id_label: 'Google Place ID',
    place_id_hint: 'Collez l’identifiant de votre fiche Google ou le lien « Écrire un avis ». Il sert à envoyer le lien d’avis après le retrait.',
    review_link_preview: 'Lien d’avis envoyé aux clients',
    feedback_label: 'Envoyer la question de satisfaction après le retrait',
    feedback_hint: 'Une question avec trois boutons sur WhatsApp, environ 15 minutes après le retrait, au plus une fois par mois par client.',
    google_link_label: 'Proposer le lien vers l’avis Google',
    google_link_hint: 'Après la réponse, le lien direct « Écrire un avis » est envoyé à tous, sans filtrage. Il nécessite le Google Place ID.',
    google_link_needs_id: 'Aucun Google Place ID : le lien d’avis ne sera pas envoyé.',
    on: 'Activé',
    off: 'Désactivé',
    add_range: 'Ajouter une plage horaire',
    remove_range: 'Retirer cette plage',
    today_hours: 'Horaires d’aujourd’hui :',
    closed_today: 'Fermé aujourd’hui.',
    bad_mode: 'Mode de numérotation invalide.',
    bad_default_lang: 'La langue par défaut doit faire partie des langues choisies.',
    bad_place_id: 'Google Place ID invalide. Collez l’identifiant ou le lien « Écrire un avis » de votre fiche Google.',
    bad_setting: 'Valeur invalide.',
    request_failed: 'Une erreur est survenue. Réessayez.',
    error_unauthorized: 'Connexion requise.',
    error_not_found: 'Introuvable.',
    error_bad_request: 'Requête invalide.',
    error_not_waiting: 'La commande n’est plus en préparation.',
    error_not_open: 'La commande est déjà clôturée.',
    error_not_ready: 'La commande n’est pas prête.',
    error_paused: 'Les enregistrements sont suspendus.',
    error_resend_cooldown: 'Attendez une minute avant de renvoyer.',
    error_not_callable: "Ce client ne peut pas être appelé sur WhatsApp : la commande doit être prête et enregistrée sur WhatsApp.",
    error_call_in_progress: 'Un appel WhatsApp est déjà en cours pour cette commande.',
    error_demo_login: 'Identifiant ou mot de passe incorrect.',
    error_wrong_code: 'Code incorrect.',
    error_locked: 'Trop de tentatives. Réessayez plus tard.',
    error_code_cooldown: 'Un code vient d’être envoyé. Patientez un instant.',
    error_bad_role: 'Rôle invalide.',
    error_already_member: 'Cette personne a déjà ce rôle.',
    error_last_owner: 'Impossible de retirer le dernier propriétaire.',
    error_bad_mode: 'Mode de numérotation invalide.',
    error_bad_name: 'Nom invalide (80 caractères au plus).',
    error_bad_lang: 'Langue invalide.',
    error_no_free_number: 'Plus aucun numéro disponible pour aujourd’hui.',
    error_session_expired: 'Session expirée. Saisissez le code du comptoir.',
    error_message_too_long: 'Message trop long.',
    error_message_placeholder: 'Une variable du message manque ou est mal écrite.',
    error_logo_too_large: 'Fichier trop lourd pour un logo.',
    error_logo_dimensions: 'Image trop grande pour un logo.',
    logo_section: 'Logo',
    logo_intro: 'Il s’affiche sur la page client, à côté du nom de l’établissement, et en haut de l’écran TV, à la place du logo RingRing.',
    logo_hint: 'PNG ou SVG, {{max}} Ko maximum.',
    logo_current: 'Logo actuel',
    logo_none: 'Aucun logo : le nom de l’établissement s’affiche seul.',
    logo_choose: 'Choisir un logo',
    logo_replace: 'Remplacer le logo',
    logo_remove: 'Retirer le logo',
    logo_uploading: 'Envoi du logo…',
    logo_saved: 'Logo enregistré',
    logo_removed: 'Logo retiré',
    logo_not_saved: 'Le logo n’a pas pu être enregistré. Réessayez.',
    logo_empty: 'Ce fichier est vide.',
    logo_too_large: 'Fichier trop lourd : {{max}} Ko maximum.',
    logo_bad_type: 'Format non accepté : choisissez un fichier PNG ou SVG.',
    logo_dimensions: 'Image trop grande : {{px}} × {{px}} pixels maximum.',
    logo_unsafe: 'Ce SVG contient un script ou un lien externe. Exportez un SVG simple ou un PNG.',
    messages_section: 'Messages aux clients',
    messages_intro: 'Réécrivez le message d’inscription et le message « commande prête » dans chaque langue. Chaque client reçoit le message dans la langue qu’il a choisie ; une langue que vous ne modifiez pas garde le texte par défaut.',
    messages_count_hint: '{{max}} caractères maximum par message, comptés sur l’aperçu : nom de l’établissement, point de retrait le plus long et dernière ligne compris.',
    message_lang: 'Langue du message',
    message_registered: 'Message d’inscription',
    message_registered_hint: 'Envoyé sur WhatsApp quand le client a activé son alerte.',
    message_ready: 'Message « commande prête »',
    message_ready_hint: 'Envoyé sur WhatsApp quand l’équipe marque la commande prête.',
    message_closing: 'Dernière ligne, ajoutée automatiquement :',
    message_counter: '{{count}} / {{max}} caractères',
    message_over: 'Trop long de {{count}} caractères',
    message_required: 'Variables obligatoires :',
    message_insert: 'Insérer une variable',
    placeholder_order_number: 'Numéro de commande',
    placeholder_venue_name: 'Nom de l’établissement',
    placeholder_pickup_point: 'Point de retrait',
    message_preview: 'Aperçu WhatsApp',
    message_sms_preview: 'SMS de secours, si le message WhatsApp n’est pas distribué en 20 secondes',
    message_sms_default: 'Ce texte ne tient pas dans un SMS : le SMS court par défaut est envoyé.',
    message_restore: 'Restaurer le texte par défaut',
    message_default_tag: 'Texte par défaut',
    message_custom_tag: 'Texte personnalisé',
    message_problem: '{{lang}}, {{message}} : {{problem}}',
    message_too_long: 'Message trop long : {{max}} caractères maximum, dernière ligne comprise.',
    message_missing_placeholder: 'Il manque la variable {{placeholder}}.',
    message_bad_placeholder: 'Variable inconnue ou mal écrite : {{placeholder}}.',
    message_link: 'Les liens ne sont pas acceptés dans ces messages.',
    poster_section: 'Affiche du comptoir',
    poster_intro: 'Imprimez l’affiche en A4 ou en A5 et posez-la près de la caisse : son QR code ouvre la page de ce comptoir.',
    poster_lang_2: 'Deuxième langue de l’affiche',
    poster_lang_hint: 'L’affiche est imprimée en {{lang}} (langue par défaut) et dans la deuxième langue choisie.',
    poster_one_lang: 'Ajoutez une deuxième langue à la page pour obtenir une affiche bilingue.',
    poster_download: 'Télécharger l’affiche {{size}}',
    poster_downloading: 'Préparation de l’affiche…',
    poster_downloaded: 'Affiche téléchargée',
    poster_failed: 'L’affiche n’a pas pu être préparée. Réessayez.',
    poster_save_first: 'L’affiche reprend les réglages enregistrés : enregistrez vos modifications avant de la télécharger.',
    bad_poster_lang: 'Choisissez la deuxième langue de l’affiche parmi les langues de la page, autre que la langue par défaut.',
    error_bad_poster_size: 'Format d’affiche invalide : A4 ou A5.',
    error_pickup_point_archived: 'Ce comptoir est archivé : il ne prend plus de commandes et n’a pas d’affiche.',
  },
  en: {
    admin_nav: 'Admin',
    order_actions: 'Order actions',
    review_qr_title: "Google review QR code",
    review_qr_heading: "One scan to leave a review.",
    review_qr_hint: "Show this code to guests or download it for your counter. It opens your venue’s Google review page.",
    review_qr_open: "Open Google review link",
    qr_download: "Download QR code (SVG)",
    feedback_nav: "Guest feedback",
    feedback_heading: "Hear from your guests.",
    feedback_intro: "Ratings and private messages received on WhatsApp, in one place.",
    feedback_received: "Responses received",
    feedback_excellent_share: "Rated Excellent",
    feedback_private_notes: "Private notes",
    feedback_unread: "Unread",
    feedback_all: "All feedback",
    feedback_pending: "Unanswered",
    feedback_period: "Period",
    feedback_period_all: "All history",
    feedback_period_7: "Last 7 days",
    feedback_period_30: "Last 30 days",
    feedback_period_90: "Last 90 days",
    feedback_rating_filter: "Rating",
    feedback_rating_all: "All ratings",
    feedback_search: "Search an order, note or counter",
    feedback_mix: "What your guests think",
    feedback_mix_hint: "Rating distribution for the selected period.",
    feedback_no_rating: "No rating",
    feedback_no_note: "The guest left a rating without a note.",
    feedback_waiting: "No response to this question yet.",
    feedback_empty_title: "Guest feedback appears here.",
    feedback_empty_body: "Your guests’ WhatsApp ratings and notes will appear in this space.",
    feedback_empty_filter: "No feedback matches these filters.",
    feedback_mark_read: "Mark as read",
    feedback_read: "Read",
    feedback_refresh: "Refresh",
    feedback_loading: "Loading feedback…",
    feedback_order: "Order #{{number}}",
    feedback_results: "{{count}} result(s)",
    feedback_page: "Page {{page}} of {{pages}}",
    feedback_previous: "Previous",
    feedback_next: "Next",
    feedback_reset: "Clear filters",
    feedback_newest: "Newest first",
    feedback_google: "Google reviews",
    feedback_google_hint: "Reviews posted on Google are available on your Google listing.",
    design_restaurant_space: "RESTAURANT SPACE",
    design_team_space: "TEAM SPACE",
    design_auth_heading: "Keep service moving.",
    design_auth_accent: "Keep your peace of mind.",
    design_auth_body: "From the counter to collection, every order has its place. Your team stays in sync while your guests enjoy the moment.",
    design_auth_footer: "Made for the counter. Designed for your team.",
    design_preview: "GUEST EXPERIENCE PREVIEW",
    design_secure_access: "Personal access to your restaurant workspace.",
    design_workspace: "Your workspace",
    design_sidebar_note: "One number. One alert. Enjoy.",
    design_hospitality: "At the pace of your service",
    design_welcome: "Welcome back.",
    design_service_heading: "Your service, at a glance.",
    design_service_intro: "Your counters, team and screens, all in one place.",
    design_live_service: "Current service",
    design_staff_intro: "Every order, at the right moment.",
    design_open_orders: "Open orders",
    design_all_orders: "All orders",
    design_search: "Search order number",
    design_no_results: "No orders match your search.",
    design_empty_hint: "New orders will appear here. You can also create a number at the counter.",
    design_connected: "Live",
    design_connecting: "Connecting",
    design_active_counters: "Active counters",
    design_team_devices: "Connected devices",
    design_guest_languages: "Guest languages",
    design_counter_tools: "Links, posters and configuration",
    design_counter_intro: "Ready for the next service.",
    design_pin_heading: "Welcome to the counter.",
    design_pin_intro: "Enter the 6-digit code from your manager to open your orders.",
    design_guest_heading: "You’re in the right place.",
    design_guest_footer: "Good moments start here.",
    design_board_waiting: "Your order is being prepared.",
    order_preparing: 'Preparing',
    order_ready: 'Ready for pickup',
    order_collected: 'Collected',
    order_cancelled: 'Cancelled',
    order_expired: 'Not collected',
    order_abandoned: 'Closed',
    message_queued: 'Queued',
    message_sending: 'Sending',
    message_sent: 'Sent',
    message_delivered: 'Delivered',
    message_failed: "Couldn't send",
    retry: 'Try again',
    edit_phone: 'Check number',
    mark_ready: 'Mark as ready',
    mark_collected: 'Mark as collected',
    ready_confirmation: 'Order #{{order_number}} marked as ready.',
    undo: 'Undo this action',
    empty: 'No orders yet.',
    orders: 'Orders',
    language: 'Language',
    tv_heading: 'Orders ready',
    tv_sub: 'Collect at the counter',
    offline: 'Connection lost. Displayed information may be out of date.',
    needs_call: 'Call out',
    new_number: 'New number',
    add_receipt: 'Add a receipt number',
    receipt_no_label: 'Receipt number',
    resend: 'Resend',
    cancel_order: 'Cancel order',
    pin_prompt: 'Counter code',
    sign_in: 'Sign in',
    get_alert: 'Get my ready alert',
    whatsapp_cta: 'Notify me on WhatsApp',
    keep_open: 'No WhatsApp? Keep this page open: it will ring when your order is ready.',
    alert_set: 'Your alert is set.',
    alert_set_body: "We'll send you a message when your order is ready.",
    your_number: 'Your order number',
    receipt_prompt: 'Enter the number printed on your receipt',
    continue: 'Continue',
    closed: 'This counter is closed for now.',
    paused_listen: 'Registrations are paused for now. Listen for your number: our team will call it out.',
    link_expired: 'This link has expired. Scan the QR code at the counter again.',
    link_invalid: 'This link is not valid. Scan the QR code at the counter to get your number.',
    help: 'Need help? Ask our team.',
    privacy_link: 'How your number is used',
    privacy_body: 'Your number is used only for service messages about this order. It is deleted 30 days after the order closes. Send STOP to stop receiving messages.',
    no_phone_alt: 'You can also wait here.',
    ready_sr: 'Order #{{order_number}} is ready for pickup.',
    pin_wrong: 'Wrong code. Attempts left: {{attempts_left}}.',
    pin_locked: 'Too many attempts. Try again in {{minutes}} min.',
    pp_not_found: 'Counter not found. Check the link or enter this venue’s code.',
    pickup_at: 'Pickup: {{pickup_point}}',
    ready_body: 'Show this number at the counter.',
    collected_body: 'Your order has been collected. Enjoy your meal!',
    cancelled_body: 'This order has been cancelled. For any question, ask our team.',
    expired_body: 'This order was not collected in time. Ask our team at the counter.',
    abandoned_body: 'This order is no longer tracked. Ask our team.',
    enable_sound: 'Enable sound',
    sound_on: 'Sound on',
    sound_hint: 'Tap once so this page can ring.',
    number_open: 'This number is already registered. Check your receipt.',
    bad_number: 'Enter a number of 1 to 6 digits.',
    too_many_open: 'You already have several orders in progress on this device. Ask our team.',
    channel_whatsapp: 'WhatsApp',
    channel_web: 'Page open',
    channel_sms: 'SMS',
    channel_push: 'Notification',
    channel_none: 'No alert',
    order_n: 'Order #{{order_number}}',
    other_orders: 'Your other orders',
    your_orders: 'Your open orders',
    another_order: 'Add another order',
    device_limit: 'You already have {{max}} open orders on this device. Collect one before adding another.',
    no_access: 'You don’t have access to this venue.',
    signin_title: 'Sign in',
    signin_intro: 'Get a sign-in code on WhatsApp.',
    phone_prompt: 'Your phone number',
    phone_hint: 'Example: 06 12 34 56 78',
    send_code: 'Send my code',
    code_prompt: 'Code received on WhatsApp',
    code_sent: 'Code sent on WhatsApp to {{phone}}. It is valid for 10 minutes.',
    code_expired: 'This code has expired. Request a new one.',
    code_not_sent: 'The code couldn’t be sent. Try again in a moment.',
    code_cooldown: 'A code was just sent. Wait {{seconds}} s before requesting another one.',
    bad_phone: 'Invalid number. Example: 06 12 34 56 78',
    resend_code: 'Resend the code',
    resend_in: 'Resend in {{seconds}} s',
    change_phone: 'Change number',
    sign_out: 'Sign out',
    sign_out_all: 'Sign out of all devices',
    signed_in_as: 'Signed in with the number',
    your_venues: 'Your venues',
    no_venue_yet: 'No venue yet.',
    role_owner: 'Owner',
    role_manager: 'Manager',
    staff_screen: 'Staff screen',
    tv_board: 'TV board',
    minutes_waiting: '{{minutes}} min',
    resend_countdown: 'Resend ({{seconds}} s)',
    resend_exhausted: 'Resend limit reached',
    call_whatsapp: 'Call on WhatsApp',
    call_auto_in: 'Automatic WhatsApp call in {{time}}',
    call_after_ready: 'Automatic WhatsApp call {{minutes}} min after "Ready"',
    call_calling: 'WhatsApp call in progress…',
    call_answered: 'WhatsApp call: answered',
    call_not_answered: 'WhatsApp call: no answer',
    call_failed: 'WhatsApp call failed',
    call_no_permission: 'WhatsApp call: the guest has not allowed calls',
    demo_reset: 'Reset the demo',
    demo_reset_question: 'Replace every order with the demo orders?',
    demo_reset_phone: 'Your WhatsApp number for orders 1 and 6 (optional)',
    demo_reset_confirm: 'Reset',
    demo_reset_keep: 'Cancel',
    demo_reset_done: 'Demo reset.',
    signin_methods: 'Sign-in method',
    signin_email: 'Email',
    signin_phone: 'Phone',
    signin_email_intro: 'Your workspace and current service, all in one place.',
    signin_identifier: 'Email or username',
    signin_secret: 'Password',
    signin_busy: 'Signing in…',
    signin_unavailable: 'Password sign-in is unavailable. Use the Phone tab.',
    cancel_confirm: 'Cancel order #{{order_number}}?',
    confirm_cancel: 'Yes, cancel',
    keep_order: 'No, keep it',
    sms_used: 'SMS used: {{used}} / {{quota}}',
    sms_quota_warning: 'almost used up',
    sms_quota_exhausted: 'quota reached',
    tv_start: 'Tap to start the board',
    tv_start_hint: 'Once only: this turns on the chime and the voice call-outs on this screen.',
    tv_sound_off: 'Sound off. Press OK or tap the screen to turn it on.',
    tv_new: 'New',
    edit_number: 'Correct the number',
    edit_number_prompt: 'New number for order #{{order_number}}',
    save_number: 'Save the number',
    edit_number_cancel: 'Keep as is',
    number_locked: 'The order is ready: the number can no longer be corrected.',
    number_corrected: 'Your order number has been corrected: {{order_number}}.',
    home_tagline: 'The virtual pager for restaurants: your guests get a WhatsApp message when their order is ready.',
    open_dashboard: 'Open the dashboard',
    setup_title: 'Set up your venue',
    setup_intro: 'Five steps, under ten minutes. You can change everything later.',
    add_venue_title: 'Add a venue',
    step_of: 'Step {{n}} of {{total}}',
    step_organization: 'Organization',
    step_venue: 'Venue',
    step_hours: 'Hours',
    step_mode: 'Numbering',
    step_languages: 'Languages',
    org_name: 'Your organization’s name',
    org_name_hint: 'The brand or the company: the name that groups your venues.',
    venue_name: 'Venue name',
    venue_name_hint: 'As it will appear in the messages sent to guests.',
    city: 'City',
    city_hint: 'Example: Casablanca',
    hours_intro: 'The counter’s opening hours. Closing after midnight (for example 18:00 to 01:00) is fine.',
    open_day: 'Open',
    closed_day: 'Closed',
    opens_at: 'Opens',
    closes_at: 'Closes',
    mode_prompt: 'How do your guests get their number?',
    mode_scan: 'Scan',
    mode_scan_desc: 'The guest scans the QR code and gets the next number on screen. The cashier writes it on the receipt.',
    mode_ticket: 'Ticket',
    mode_ticket_desc: 'Your till already prints a number: the guest types it after scanning the QR code.',
    languages_prompt: 'Customer page languages',
    languages_hint: 'Pick at least one language. The default one shows on first load.',
    default_lang: 'Default language',
    back: 'Back',
    next: 'Continue',
    finish: 'Finish',
    saving: 'Saving…',
    bad_org_name: 'Enter your organization’s name (80 characters at most).',
    bad_venue_name: 'Enter the venue name (80 characters at most).',
    bad_city: 'Enter the city.',
    bad_hours: 'Check the hours: at least one open day, and a closing time different from the opening time.',
    bad_languages: 'Pick at least one language.',
    dashboard: 'Dashboard',
    venue_switcher: 'Venue shown',
    venue_details: 'Venue',
    mode_label: 'Numbering',
    languages_label: 'Languages',
    default_lang_label: 'Default language',
    hours_label: 'Hours',
    pickup_points: 'Pickup points',
    pickup_points_intro: 'Each pickup point has its own numbering, QR code and TV board.',
    add_pickup_point: 'Add a pickup point',
    pickup_point_name: 'Pickup point name',
    pickup_point_default: 'Counter 1',
    customer_url: 'QR code link',
    open_link: 'Open',
    copy_link: 'Copy link',
    copied: 'Link copied',
    rename: 'Rename',
    save: 'Save',
    cancel: 'Cancel',
    archive: 'Archive',
    unarchive: 'Reactivate',
    archived: 'Archived',
    archived_points: 'Archived pickup points',
    last_pickup_point: 'The last active pickup point cannot be archived.',
    add_venue: 'Add a venue',
    account: 'Account',
    day_mon: 'Monday',
    day_tue: 'Tuesday',
    day_wed: 'Wednesday',
    day_thu: 'Thursday',
    day_fri: 'Friday',
    day_sat: 'Saturday',
    day_sun: 'Sunday',
    kitchen_view: 'Kitchen view',
    pickup_point_filter: 'Counter shown',
    all_pickup_points: 'All counters',
    open_orders: '{{count}} open',
    tv_callout: 'Number {{order_number}}',
    tv_voice_on: 'Voice: {{langs}}',
    tv_voice_off: 'No voice on this screen: chime only',
    voice_lang_2: 'Second language for voice call-outs',
    voice_lang_none: 'None',
    default_lang_is: 'Default language: {{lang}}',
    saved: 'Saved',
    send_by_email: 'Send the code by email instead',
    send_by_whatsapp: 'Send the code on WhatsApp instead',
    code_sent_email: 'Code sent by email to {{email}}. It is valid for 10 minutes.',
    code_prompt_email: 'Code received by email',
    email_hint: 'Check the spam folder too.',
    no_backup_email: 'No backup email for this number. Sign in with WhatsApp, then add a backup email in your account.',
    backup_email: 'Backup email',
    backup_email_intro: 'To receive your sign-in code by email when WhatsApp is not available.',
    backup_email_none: 'No backup email.',
    email_prompt: 'Your email address',
    send_email_code: 'Send a confirmation code',
    email_code_sent: 'Code sent to {{email}}. Enter it to confirm this address.',
    confirm_email: 'Confirm the address',
    email_confirmed: 'Backup email saved.',
    email_removed: 'Backup email removed.',
    remove_email: 'Remove the backup email',
    change_email: 'Change the address',
    bad_email: 'Invalid email address. Example: name@example.ma',
    staff_pin_section: 'Counter code',
    staff_pin_intro: 'Your team enters this 6-digit code once per device to open the counter screen. Each device stays signed in for 30 days.',
    no_staff_pin: 'No code set.',
    staff_pin_set: 'Code set.',
    staff_pin_set_on: 'Code set on {{date}}.',
    generate_pin: 'Generate a code',
    regenerate_pin: 'Regenerate the code',
    regenerate_warning: 'Regenerating the code signs out {{count}} device(s). The old code stops working.',
    confirm_regenerate: 'Yes, regenerate',
    new_pin_title: 'New counter code',
    new_pin_note: 'Write it down now: it will not be shown again.',
    pin_noted: 'I have noted the code',
    devices: 'Signed-in devices',
    devices_intro: 'Every device that entered the code. Revoking a device sends it back to the code form within a minute.',
    no_devices: 'No device signed in yet.',
    former_devices: 'Former devices',
    revoke: 'Revoke',
    device_active: 'Signed in',
    device_revoked: 'Revoked',
    device_signed_out_pin: 'Signed out by the new code',
    device_replaced: 'Replaced by a new sign-in',
    device_expired: 'Expired',
    first_seen: 'First sign-in',
    last_seen: 'Last activity',
    seen_just_now: 'just now',
    seen_minutes_ago: '{{minutes}} min ago',
    seen_hours_ago: '{{hours}} h ago',
    seen_days_ago: '{{days}} d ago',
    unknown_device: 'Unknown device',
    signed_in_from: 'Signed in from {{pickup_point}}',
    device_revoked_message: 'This device has been signed out. Enter the counter code to continue.',
    board_link: 'TV board link',
    board_link_intro: 'Open this link on the TV at the counter: it shows the numbers without a code and allows no action. Anyone who has this link can see the board; revoke it if it has been shared.',
    copy_board_link: 'Copy the TV board link',
    revoke_board_link: 'Revoke this link',
    revoke_board_link_warning: 'The screen that uses this link stops within a minute. A new link appears here, to open on the TV.',
    confirm_revoke_board_link: 'Yes, revoke',
    board_link_renewed: 'Old link revoked. Here is the new one: open it on the TV.',
    tv_link_missing_title: 'Board link required',
    tv_link_missing_body: "This screen opens only with the TV board link. Copy it from the manager's dashboard, under the counter.",
    tv_link_invalid_title: 'Board link not valid',
    tv_link_invalid_body: "This link may have been revoked. Open the current link from the manager's dashboard, under the counter.",
    settings: 'Settings',
    settings_title: 'Venue settings',
    settings_intro: 'Changes apply at once to the customer page, the staff screen and the TV board. Open orders are kept.',
    back_to_dashboard: 'Back to the dashboard',
    settings_saved: 'Settings saved',
    registrations_label: 'QR code registrations',
    pause_label: 'Pause registrations',
    pause_hint: 'During a rush or an outage: the QR code hands out no number and the customer page asks guests to listen for their number. The staff screen and the TV board keep working.',
    paused_tag: 'Registrations paused',
    active_tag: 'Registrations on',
    feedback_section: 'Guest feedback',
    place_id_label: 'Google Place ID',
    place_id_hint: 'Paste the ID of your Google listing or its “Write a review” link. It is used to send the review link after pickup.',
    review_link_preview: 'Review link sent to guests',
    feedback_label: 'Send the satisfaction question after pickup',
    feedback_hint: 'One WhatsApp question with three buttons, about 15 minutes after pickup, at most once a month per guest.',
    google_link_label: 'Offer the Google review link',
    google_link_hint: 'After the answer, the direct “Write a review” link is sent to everyone, with no filtering. It needs the Google Place ID.',
    google_link_needs_id: 'No Google Place ID: the review link will not be sent.',
    on: 'On',
    off: 'Off',
    add_range: 'Add a time range',
    remove_range: 'Remove this range',
    today_hours: 'Today’s hours:',
    closed_today: 'Closed today.',
    bad_mode: 'Invalid numbering mode.',
    bad_default_lang: 'The default language must be one of the chosen languages.',
    bad_place_id: 'Invalid Google Place ID. Paste the ID or the “Write a review” link of your Google listing.',
    bad_setting: 'Invalid value.',
    request_failed: 'Something went wrong. Try again.',
    error_unauthorized: 'Sign-in required.',
    error_not_found: 'Not found.',
    error_bad_request: 'Invalid request.',
    error_not_waiting: 'The order is no longer being prepared.',
    error_not_open: 'The order is already closed.',
    error_not_ready: 'The order is not ready.',
    error_paused: 'Registrations are paused.',
    error_resend_cooldown: 'Wait a minute before resending.',
    error_not_callable: 'This guest cannot be called on WhatsApp: the order must be ready and registered on WhatsApp.',
    error_call_in_progress: 'A WhatsApp call is already in progress for this order.',
    error_demo_login: 'Wrong login or password.',
    error_wrong_code: 'Wrong code.',
    error_locked: 'Too many attempts. Try again later.',
    error_code_cooldown: 'A code was just sent. Wait a moment.',
    error_bad_role: 'Invalid role.',
    error_already_member: 'This person already has that role.',
    error_last_owner: 'The last owner cannot be removed.',
    error_bad_mode: 'Invalid numbering mode.',
    error_bad_name: 'Invalid name (80 characters at most).',
    error_bad_lang: 'Invalid language.',
    error_no_free_number: 'No number left for today.',
    error_session_expired: 'Session expired. Enter the counter code.',
    error_message_too_long: 'Message too long.',
    error_message_placeholder: 'A variable of the message is missing or misspelt.',
    error_logo_too_large: 'File too large for a logo.',
    error_logo_dimensions: 'Image too large for a logo.',
    logo_section: 'Logo',
    logo_intro: 'It shows on the customer page next to the venue name, and at the top of the TV board in place of the RingRing logo.',
    logo_hint: 'PNG or SVG, {{max}} KB maximum.',
    logo_current: 'Current logo',
    logo_none: 'No logo: the venue name shows on its own.',
    logo_choose: 'Choose a logo',
    logo_replace: 'Replace the logo',
    logo_remove: 'Remove the logo',
    logo_uploading: 'Uploading the logo…',
    logo_saved: 'Logo saved',
    logo_removed: 'Logo removed',
    logo_not_saved: 'The logo could not be saved. Try again.',
    logo_empty: 'This file is empty.',
    logo_too_large: 'File too large: {{max}} KB maximum.',
    logo_bad_type: 'Format not accepted: choose a PNG or SVG file.',
    logo_dimensions: 'Image too large: {{px}} × {{px}} pixels maximum.',
    logo_unsafe: 'This SVG contains a script or an external link. Export a plain SVG or a PNG.',
    messages_section: 'Messages to guests',
    messages_intro: 'Rewrite the registration message and the "order ready" message in each language. Each guest gets the message in the language they chose; a language you do not edit keeps the default text.',
    messages_count_hint: '{{max}} characters maximum per message, counted on the preview: venue name, longest pickup point name and last line included.',
    message_lang: 'Message language',
    message_registered: 'Registration message',
    message_registered_hint: 'Sent on WhatsApp once the guest has set the alert.',
    message_ready: '"Order ready" message',
    message_ready_hint: 'Sent on WhatsApp when staff mark the order ready.',
    message_closing: 'Last line, added automatically:',
    message_counter: '{{count}} / {{max}} characters',
    message_over: '{{count}} characters too long',
    message_required: 'Required variables:',
    message_insert: 'Insert a variable',
    placeholder_order_number: 'Order number',
    placeholder_venue_name: 'Venue name',
    placeholder_pickup_point: 'Pickup point',
    message_preview: 'WhatsApp preview',
    message_sms_preview: 'Backup SMS, if the WhatsApp message is not delivered within 20 seconds',
    message_sms_default: 'This text does not fit in one SMS: the short default SMS is sent.',
    message_restore: 'Restore the default text',
    message_default_tag: 'Default text',
    message_custom_tag: 'Custom text',
    message_problem: '{{lang}}, {{message}}: {{problem}}',
    message_too_long: 'Message too long: {{max}} characters maximum, last line included.',
    message_missing_placeholder: 'The variable {{placeholder}} is missing.',
    message_bad_placeholder: 'Unknown or misspelt variable: {{placeholder}}.',
    message_link: 'Links are not accepted in these messages.',
    poster_section: 'Counter poster',
    poster_intro: 'Print the poster in A4 or A5 and put it by the till: its QR code opens this counter’s page.',
    poster_lang_2: 'Poster second language',
    poster_lang_hint: 'The poster is printed in {{lang}} (default language) and in the second language you pick.',
    poster_one_lang: 'Add a second page language to get a bilingual poster.',
    poster_download: 'Download the {{size}} poster',
    poster_downloading: 'Preparing the poster…',
    poster_downloaded: 'Poster downloaded',
    poster_failed: 'The poster could not be prepared. Try again.',
    poster_save_first: 'The poster uses the saved settings: save your changes before downloading it.',
    bad_poster_lang: 'Pick the poster’s second language among the page languages, other than the default one.',
    error_bad_poster_size: 'Invalid poster size: A4 or A5.',
    error_pickup_point_archived: 'This counter is archived: it takes no orders and has no poster.',
  },
  ar: {
    admin_nav: 'الإدارة',
    order_actions: 'إجراءات الطلب',
    review_qr_title: "رمز QR لتقييم Google",
    review_qr_heading: "مسح واحد لكتابة تقييم.",
    review_qr_hint: "اعرض هذا الرمز لزبائنك أو نزّله لوضعه على المنضدة. يفتح صفحة تقييم نشاطك التجاري على Google.",
    review_qr_open: "فتح رابط تقييم Google",
    qr_download: "تنزيل رمز QR بصيغة SVG",
    feedback_nav: "آراء الزبائن",
    feedback_heading: "استمع إلى زبائنك.",
    feedback_intro: "التقييمات والرسائل الخاصة الواردة عبر واتساب، في مكان واحد.",
    feedback_received: "الآراء الواردة",
    feedback_excellent_share: "تقييمات «ممتاز»",
    feedback_private_notes: "الرسائل الخاصة",
    feedback_unread: "غير المقروءة",
    feedback_all: "جميع الآراء",
    feedback_pending: "دون إجابة",
    feedback_period: "الفترة",
    feedback_period_all: "كامل السجل",
    feedback_period_7: "آخر 7 أيام",
    feedback_period_30: "آخر 30 يومًا",
    feedback_period_90: "آخر 90 يومًا",
    feedback_rating_filter: "التقييم",
    feedback_rating_all: "جميع التقييمات",
    feedback_search: "ابحث عن رقم طلب أو رسالة أو منضدة",
    feedback_mix: "ما رأي زبائنك؟",
    feedback_mix_hint: "توزيع التقييمات خلال الفترة المحددة.",
    feedback_no_rating: "دون تقييم",
    feedback_no_note: "ترك الزبون تقييمًا دون رسالة.",
    feedback_waiting: "لم تصل إجابة عن هذا السؤال بعد.",
    feedback_empty_title: "تظهر آراء الزبائن هنا.",
    feedback_empty_body: "ستظهر تقييمات زبائنك ورسائلهم عبر واتساب في هذه الصفحة.",
    feedback_empty_filter: "لا توجد آراء تطابق هذه المرشحات.",
    feedback_mark_read: "وضع علامة مقروء",
    feedback_read: "مقروء",
    feedback_refresh: "تحديث",
    feedback_loading: "جارٍ تحميل الآراء…",
    feedback_order: "الطلب رقم {{number}}",
    feedback_results: "عدد النتائج: {{count}}",
    feedback_page: "الصفحة {{page}} من {{pages}}",
    feedback_previous: "السابق",
    feedback_next: "التالي",
    feedback_reset: "مسح المرشحات",
    feedback_newest: "الأحدث أولًا",
    feedback_google: "تقييمات Google",
    feedback_google_hint: "يمكن الاطلاع على التقييمات المنشورة على Google في صفحة نشاطك التجاري.",
    design_restaurant_space: "مساحة المطعم",
    design_team_space: "مساحة الفريق",
    design_auth_heading: "الخدمة مستمرة.",
    design_auth_accent: "وراحة البال حاضرة.",
    design_auth_body: "من المنضدة إلى الاستلام، لكل طلب مساره. يتابع فريقكم الخدمة ويستمتع زبائنكم بوقتهم.",
    design_auth_footer: "للمنضدة، ولفريقكم.",
    design_preview: "معاينة تجربة الزبون",
    design_secure_access: "وصول شخصي إلى مساحة مطعمكم.",
    design_workspace: "مساحتكم",
    design_sidebar_note: "رقم وتنبيه، ثم وجبة هنيئة.",
    design_hospitality: "بإيقاع خدمتكم",
    design_welcome: "مرحبًا بعودتكم.",
    design_service_heading: "خدمتكم في لمحة.",
    design_service_intro: "مناضدكم وفريقكم وشاشاتكم في مكان واحد.",
    design_live_service: "الخدمة الحالية",
    design_staff_intro: "كل طلب في وقته.",
    design_open_orders: "الطلبات الحالية",
    design_all_orders: "الكل",
    design_search: "البحث عن رقم طلب",
    design_no_results: "لا توجد طلبات تطابق بحثكم.",
    design_empty_hint: "ستظهر الطلبات الجديدة هنا. يمكنكم أيضًا إنشاء رقم من المنضدة.",
    design_connected: "مباشر",
    design_connecting: "جارٍ الاتصال",
    design_active_counters: "المناضد النشطة",
    design_team_devices: "الأجهزة المتصلة",
    design_guest_languages: "اللغات المتاحة",
    design_counter_tools: "الروابط والملصقات والإعدادات",
    design_counter_intro: "جاهزون للخدمة القادمة.",
    design_pin_heading: "مرحبًا بكم في المنضدة.",
    design_pin_intro: "أدخلوا الرمز المكوّن من 6 أرقام الذي يقدّمه المسؤول للوصول إلى الطلبات.",
    design_guest_heading: "أنتم في المكان المناسب.",
    design_guest_footer: "اللحظات الطيبة تبدأ هنا.",
    design_board_waiting: "طلبكم قيد التحضير.",
    order_preparing: 'قيد التحضير',
    order_ready: 'جاهز للاستلام',
    order_collected: 'تم الاستلام',
    order_cancelled: 'تم الإلغاء',
    order_expired: 'لم يُستلم',
    order_abandoned: 'تم الإغلاق',
    message_queued: 'في انتظار الإرسال',
    message_sending: 'جارٍ الإرسال',
    message_sent: 'تم الإرسال',
    message_delivered: 'تم التسليم',
    message_failed: 'تعذّر الإرسال',
    retry: 'إعادة المحاولة',
    edit_phone: 'التحقّق من الرقم',
    mark_ready: 'تحديد الطلب كجاهز',
    mark_collected: 'تأكيد الاستلام',
    ready_confirmation: 'تم تحديد الطلب رقم {{order_number}} كجاهز.',
    undo: 'التراجع عن هذا الإجراء',
    empty: 'لا توجد طلبات حاليًا.',
    orders: 'الطلبات',
    language: 'اللغة',
    tv_heading: 'الطلبات الجاهزة',
    tv_sub: 'الاستلام من المنضدة',
    offline: 'انقطع الاتصال. قد تكون المعلومات المعروضة غير محدّثة.',
    needs_call: 'يُنادى عليه',
    new_number: 'رقم جديد',
    add_receipt: 'إضافة رقم تذكرة',
    receipt_no_label: 'رقم التذكرة',
    resend: 'إعادة الإرسال',
    cancel_order: 'إلغاء الطلب',
    pin_prompt: 'رمز المنضدة',
    sign_in: 'تسجيل الدخول',
    get_alert: 'تلقّي تنبيه جاهزية الطلب',
    whatsapp_cta: 'أبلغوني عبر واتساب',
    keep_open: 'لا تستخدم واتساب؟ أبقِ هذه الصفحة مفتوحة، وسترنّ عندما يصبح طلبك جاهزًا.',
    alert_set: 'تم تفعيل التنبيه.',
    alert_set_body: 'سنرسل إليك رسالة عندما يصبح طلبك جاهزًا.',
    your_number: 'رقم طلبك',
    receipt_prompt: 'أدخل الرقم المطبوع على تذكرتك',
    continue: 'متابعة',
    closed: 'هذه المنضدة مغلقة حاليًا.',
    paused_listen: 'التسجيل متوقف مؤقتًا. انتبه لرقمك: سيناديه فريقنا.',
    link_expired: 'انتهت صلاحية هذا الرابط. امسح رمز QR الموجود على المنضدة من جديد.',
    link_invalid: 'هذا الرابط غير صالح. امسح رمز QR على المنضدة للحصول على رقمك.',
    help: 'هل تحتاج إلى مساعدة؟ تواصل مع فريقنا.',
    privacy_link: 'كيف نستخدم رقم هاتفك',
    privacy_body: 'يُستخدم رقمك فقط لرسائل الخدمة المتعلقة بهذا الطلب، ويُحذف بعد 30 يومًا من إغلاق الطلب. أرسل STOP للتوقف عن تلقي الرسائل.',
    no_phone_alt: 'يمكنك أيضًا الانتظار هنا.',
    ready_sr: 'الطلب رقم {{order_number}} جاهز للاستلام.',
    pin_wrong: 'الرمز غير صحيح. المحاولات المتبقية: {{attempts_left}}.',
    pin_locked: 'محاولات كثيرة. أعد المحاولة بعد {{minutes}} دقيقة.',
    pp_not_found: 'المنضدة غير موجودة. تحقّق من الرابط أو أدخل رمز هذا المطعم.',
    pickup_at: 'الاستلام من {{pickup_point}}',
    ready_body: 'أظهر هذا الرقم عند المنضدة.',
    collected_body: 'تم استلام طلبك. بالهناء والشفاء!',
    cancelled_body: 'تم إلغاء هذا الطلب. لأي استفسار، تواصل مع فريقنا.',
    expired_body: 'لم يُستلم هذا الطلب في الوقت المحدد. تواصل مع فريقنا عند المنضدة.',
    abandoned_body: 'لم يعد هذا الطلب متابعًا. تواصل مع فريقنا.',
    enable_sound: 'تفعيل الصوت',
    sound_on: 'الصوت مفعّل',
    sound_hint: 'اضغط مرة واحدة لتتمكّن هذه الصفحة من الرنين.',
    number_open: 'هذا الرقم مسجّل بالفعل. تحقّق من تذكرتك.',
    bad_number: 'أدخل رقمًا من 1 إلى 6 أرقام.',
    too_many_open: 'لديك بالفعل عدة طلبات قيد التحضير على هذا الجهاز. تواصل مع فريقنا.',
    channel_whatsapp: 'WhatsApp',
    channel_web: 'الصفحة مفتوحة',
    channel_sms: 'رسالة نصية',
    channel_push: 'إشعار',
    channel_none: 'بدون تنبيه',
    order_n: 'الطلب رقم {{order_number}}',
    other_orders: 'طلباتك الأخرى',
    your_orders: 'طلباتك الحالية',
    another_order: 'إضافة طلب آخر',
    device_limit: 'لديك بالفعل {{max}} طلبات قيد التنفيذ على هذا الجهاز. استلم أحدها قبل إضافة طلب جديد.',
    no_access: 'ليس لديك صلاحية الوصول إلى هذا المطعم.',
    signin_title: 'تسجيل الدخول',
    signin_intro: 'احصل على رمز تسجيل الدخول عبر واتساب.',
    phone_prompt: 'رقم هاتفك',
    phone_hint: 'مثال: 06 12 34 56 78',
    send_code: 'إرسال الرمز',
    code_prompt: 'الرمز الذي وصلك عبر واتساب',
    code_sent: 'تم إرسال الرمز عبر واتساب إلى {{phone}}. وهو صالح لمدة 10 دقائق.',
    code_expired: 'انتهت صلاحية هذا الرمز. اطلب رمزًا جديدًا.',
    code_not_sent: 'تعذّر إرسال الرمز. أعد المحاولة بعد قليل.',
    code_cooldown: 'تم إرسال رمز للتو. انتظر {{seconds}} ثانية قبل طلب رمز آخر.',
    bad_phone: 'رقم غير صالح. مثال: 06 12 34 56 78',
    resend_code: 'إعادة إرسال الرمز',
    resend_in: 'إعادة الإرسال بعد {{seconds}} ثانية',
    change_phone: 'تغيير الرقم',
    sign_out: 'تسجيل الخروج',
    sign_out_all: 'تسجيل الخروج من جميع الأجهزة',
    signed_in_as: 'متصل بالرقم',
    your_venues: 'مطاعمك',
    no_venue_yet: 'لا يوجد مطعم بعد.',
    role_owner: 'المالك',
    role_manager: 'المدير',
    staff_screen: 'شاشة الفريق',
    tv_board: 'شاشة التلفاز',
    minutes_waiting: '{{minutes}} د',
    resend_countdown: 'إعادة الإرسال ({{seconds}} ث)',
    resend_exhausted: 'تم بلوغ حد إعادة الإرسال',
    call_whatsapp: 'الاتصال عبر واتساب',
    call_auto_in: 'اتصال تلقائي عبر واتساب بعد {{time}}',
    call_after_ready: 'اتصال تلقائي عبر واتساب بعد {{minutes}} دقائق من « جاهز »',
    call_calling: 'جارٍ الاتصال عبر واتساب…',
    call_answered: 'اتصال واتساب: تم الرد',
    call_not_answered: 'اتصال واتساب: لا يوجد رد',
    call_failed: 'تعذّر الاتصال عبر واتساب',
    call_no_permission: 'اتصال واتساب: لم يسمح العميل بالمكالمات',
    demo_reset: 'إعادة ضبط العرض التوضيحي',
    demo_reset_question: 'هل تريد استبدال كل الطلبات بطلبات العرض التوضيحي؟',
    demo_reset_phone: 'رقم واتساب الخاص بك للطلبين 1 و6 (اختياري)',
    demo_reset_confirm: 'إعادة الضبط',
    demo_reset_keep: 'إلغاء',
    demo_reset_done: 'تمت إعادة ضبط العرض التوضيحي.',
    signin_methods: 'طريقة تسجيل الدخول',
    signin_email: 'البريد الإلكتروني',
    signin_phone: 'الهاتف',
    signin_email_intro: 'ادخل إلى مساحة عملك وتابع الطلبات الحالية.',
    signin_identifier: 'البريد الإلكتروني أو اسم المستخدم',
    signin_secret: 'كلمة المرور',
    signin_busy: 'جارٍ تسجيل الدخول…',
    signin_unavailable: 'تسجيل الدخول بكلمة مرور غير متاح. استخدم علامة تبويب الهاتف.',
    cancel_confirm: 'إلغاء الطلب رقم {{order_number}}؟',
    confirm_cancel: 'نعم، إلغاء',
    keep_order: 'لا، إبقاء الطلب',
    sms_used: 'الرسائل النصية المستخدمة: {{used}} / {{quota}}',
    sms_quota_warning: 'على وشك النفاد',
    sms_quota_exhausted: 'تم بلوغ الحد',
    tv_start: 'اضغط لبدء العرض',
    tv_start_hint: 'مرة واحدة فقط: يُفعّل ذلك الرنين والإعلانات الصوتية على هذه الشاشة.',
    tv_sound_off: 'الصوت متوقف. اضغط OK أو المس الشاشة لتفعيله.',
    tv_new: 'جديد',
    edit_number: 'تصحيح الرقم',
    edit_number_prompt: 'الرقم الجديد للطلب رقم {{order_number}}',
    save_number: 'حفظ الرقم',
    edit_number_cancel: 'الإبقاء على الرقم',
    number_locked: 'الطلب جاهز: لم يعد بالإمكان تصحيح الرقم.',
    number_corrected: 'تم تصحيح رقم طلبك: {{order_number}}.',
    home_tagline: 'جهاز النداء الافتراضي للمطاعم: يتلقى زبائنك رسالة على واتساب عندما يصبح طلبهم جاهزًا.',
    open_dashboard: 'فتح لوحة التحكم',
    setup_title: 'إعداد مطعمك',
    setup_intro: 'خمس خطوات في أقل من عشر دقائق. يمكنك تعديل كل شيء لاحقًا.',
    add_venue_title: 'إضافة مطعم',
    step_of: 'الخطوة {{n}} من {{total}}',
    step_organization: 'المؤسسة',
    step_venue: 'المطعم',
    step_hours: 'أوقات العمل',
    step_mode: 'الترقيم',
    step_languages: 'اللغات',
    org_name: 'اسم مؤسستك',
    org_name_hint: 'العلامة التجارية أو الشركة: الاسم الذي يجمع مطاعمك.',
    venue_name: 'اسم المطعم',
    venue_name_hint: 'كما سيظهر في الرسائل المرسلة إلى الزبائن.',
    city: 'المدينة',
    city_hint: 'مثال: الدار البيضاء',
    hours_intro: 'أوقات عمل المنضدة. يُقبل الإغلاق بعد منتصف الليل (مثلًا من 18:00 إلى 01:00).',
    open_day: 'مفتوح',
    closed_day: 'مغلق',
    opens_at: 'الفتح',
    closes_at: 'الإغلاق',
    mode_prompt: 'كيف يحصل زبائنك على رقمهم؟',
    mode_scan: 'مسح',
    mode_scan_desc: 'يمسح الزبون رمز QR ويحصل على الرقم التالي على الشاشة. يكتبه أمين الصندوق على التذكرة.',
    mode_ticket: 'تذكرة',
    mode_ticket_desc: 'صندوقك يطبع رقمًا بالفعل: يدخله الزبون بعد مسح رمز QR.',
    languages_prompt: 'لغات صفحة الزبون',
    languages_hint: 'اختر لغة واحدة على الأقل. تظهر اللغة الافتراضية عند أول تحميل.',
    default_lang: 'اللغة الافتراضية',
    back: 'رجوع',
    next: 'متابعة',
    finish: 'إنهاء',
    saving: 'جارٍ الحفظ…',
    bad_org_name: 'أدخل اسم مؤسستك (80 حرفًا على الأكثر).',
    bad_venue_name: 'أدخل اسم المطعم (80 حرفًا على الأكثر).',
    bad_city: 'أدخل المدينة.',
    bad_hours: 'تحقّق من أوقات العمل: يوم مفتوح واحد على الأقل، ووقت إغلاق مختلف عن وقت الفتح.',
    bad_languages: 'اختر لغة واحدة على الأقل.',
    dashboard: 'لوحة التحكم',
    venue_switcher: 'المطعم المعروض',
    venue_details: 'المطعم',
    mode_label: 'الترقيم',
    languages_label: 'اللغات',
    default_lang_label: 'اللغة الافتراضية',
    hours_label: 'أوقات العمل',
    pickup_points: 'المناضد',
    pickup_points_intro: 'لكل منضدة ترقيمها الخاص ورمز QR وشاشة تلفاز.',
    add_pickup_point: 'إضافة منضدة',
    pickup_point_name: 'اسم المنضدة',
    pickup_point_default: 'المنضدة 1',
    customer_url: 'رابط رمز QR',
    open_link: 'فتح',
    copy_link: 'نسخ الرابط',
    copied: 'تم نسخ الرابط',
    rename: 'إعادة التسمية',
    save: 'حفظ',
    cancel: 'إلغاء',
    archive: 'أرشفة',
    unarchive: 'إعادة التفعيل',
    archived: 'مؤرشف',
    archived_points: 'المناضد المؤرشفة',
    last_pickup_point: 'لا يمكن أرشفة آخر منضدة نشطة.',
    add_venue: 'إضافة مطعم',
    account: 'الحساب',
    day_mon: 'الاثنين',
    day_tue: 'الثلاثاء',
    day_wed: 'الأربعاء',
    day_thu: 'الخميس',
    day_fri: 'الجمعة',
    day_sat: 'السبت',
    day_sun: 'الأحد',
    kitchen_view: 'عرض المطبخ',
    pickup_point_filter: 'المنضدة المعروضة',
    all_pickup_points: 'جميع المناضد',
    open_orders: '{{count}} قيد التنفيذ',
    tv_callout: 'الرقم {{order_number}}',
    tv_voice_on: 'الصوت: {{langs}}',
    tv_voice_off: 'لا يوجد صوت على هذه الشاشة: الرنين فقط',
    voice_lang_2: 'اللغة الثانية للإعلانات الصوتية',
    voice_lang_none: 'بدون',
    default_lang_is: 'اللغة الافتراضية: {{lang}}',
    saved: 'تم الحفظ',
    send_by_email: 'إرسال الرمز عبر البريد الإلكتروني',
    send_by_whatsapp: 'إرسال الرمز عبر واتساب',
    code_sent_email: 'تم إرسال الرمز عبر البريد الإلكتروني إلى {{email}}. وهو صالح لمدة 10 دقائق.',
    code_prompt_email: 'الرمز الذي وصلك عبر البريد الإلكتروني',
    email_hint: 'تحقّق أيضًا من مجلد الرسائل غير المرغوب فيها.',
    no_backup_email: 'لا يوجد بريد إلكتروني احتياطي لهذا الرقم. سجّل الدخول عبر واتساب، ثم أضف بريدًا إلكترونيًا احتياطيًا في حسابك.',
    backup_email: 'البريد الإلكتروني الاحتياطي',
    backup_email_intro: 'لتلقّي رمز تسجيل الدخول عبر البريد الإلكتروني عندما لا يكون واتساب متاحًا.',
    backup_email_none: 'لا يوجد بريد إلكتروني احتياطي.',
    email_prompt: 'عنوان بريدك الإلكتروني',
    send_email_code: 'إرسال رمز التأكيد',
    email_code_sent: 'تم إرسال الرمز إلى {{email}}. أدخله لتأكيد هذا العنوان.',
    confirm_email: 'تأكيد العنوان',
    email_confirmed: 'تم حفظ البريد الإلكتروني الاحتياطي.',
    email_removed: 'تم حذف البريد الإلكتروني الاحتياطي.',
    remove_email: 'حذف البريد الإلكتروني الاحتياطي',
    change_email: 'تغيير العنوان',
    bad_email: 'عنوان بريد إلكتروني غير صالح. مثال: name@example.ma',
    staff_pin_section: 'رمز المنضدة',
    staff_pin_intro: 'يُدخل فريقك هذا الرمز المكوّن من 6 أرقام مرة واحدة على كل جهاز لفتح شاشة المنضدة. يبقى كل جهاز متصلًا لمدة 30 يومًا.',
    no_staff_pin: 'لم يُحدَّد أي رمز.',
    staff_pin_set: 'الرمز محدَّد.',
    staff_pin_set_on: 'حُدِّد الرمز بتاريخ {{date}}.',
    generate_pin: 'إنشاء رمز',
    regenerate_pin: 'إعادة إنشاء الرمز',
    regenerate_warning: 'إعادة إنشاء الرمز تُسجّل خروج {{count}} جهاز/أجهزة. يتوقف الرمز القديم عن العمل.',
    confirm_regenerate: 'نعم، أعد الإنشاء',
    new_pin_title: 'رمز المنضدة الجديد',
    new_pin_note: 'دوّنه الآن: لن يُعرض مرة أخرى.',
    pin_noted: 'دوّنت الرمز',
    devices: 'الأجهزة المتصلة',
    devices_intro: 'كل جهاز أدخل الرمز. إلغاء جهاز يعيده إلى نموذج الرمز في أقل من دقيقة.',
    no_devices: 'لا يوجد جهاز متصل حتى الآن.',
    former_devices: 'الأجهزة السابقة',
    revoke: 'إلغاء',
    device_active: 'متصل',
    device_revoked: 'مُلغى',
    device_signed_out_pin: 'سُجِّل خروجه بالرمز الجديد',
    device_replaced: 'استُبدل باتصال جديد',
    device_expired: 'منتهي الصلاحية',
    first_seen: 'أول اتصال',
    last_seen: 'آخر نشاط',
    seen_just_now: 'الآن',
    seen_minutes_ago: 'قبل {{minutes}} دقيقة',
    seen_hours_ago: 'قبل {{hours}} ساعة',
    seen_days_ago: 'قبل {{days}} يوم',
    unknown_device: 'جهاز غير معروف',
    signed_in_from: 'متصل من {{pickup_point}}',
    device_revoked_message: 'تم تسجيل خروج هذا الجهاز. أدخل رمز المنضدة للمتابعة.',
    board_link: 'رابط شاشة التلفاز',
    board_link_intro: 'افتح هذا الرابط على تلفاز المنضدة: يعرض الأرقام دون رمز ولا يتيح أي إجراء. يرى الشاشةَ كلُّ من لديه هذا الرابط؛ ألغِه إذا تم تداوله.',
    copy_board_link: 'نسخ رابط شاشة التلفاز',
    revoke_board_link: 'إلغاء هذا الرابط',
    revoke_board_link_warning: 'تتوقف الشاشة التي تستخدم هذا الرابط خلال دقيقة. يظهر هنا رابط جديد لفتحه على التلفاز.',
    confirm_revoke_board_link: 'نعم، ألغِ الرابط',
    board_link_renewed: 'تم إلغاء الرابط القديم. هذا هو الرابط الجديد: افتحه على التلفاز.',
    tv_link_missing_title: 'رابط العرض مطلوب',
    tv_link_missing_body: 'لا تُفتح هذه الشاشة إلا برابط شاشة التلفاز. انسخه من لوحة تحكم المدير، تحت المنضدة.',
    tv_link_invalid_title: 'رابط العرض غير صالح',
    tv_link_invalid_body: 'ربما تم إلغاء هذا الرابط. افتح الرابط الحالي من لوحة تحكم المدير، تحت المنضدة.',
    settings: 'الإعدادات',
    settings_title: 'إعدادات المطعم',
    settings_intro: 'تُطبّق التغييرات فورًا على صفحة الزبون وشاشة الفريق وشاشة التلفاز. تبقى الطلبات الجارية كما هي.',
    back_to_dashboard: 'العودة إلى لوحة التحكم',
    settings_saved: 'تم حفظ الإعدادات',
    registrations_label: 'التسجيل عبر رمز QR',
    pause_label: 'إيقاف التسجيل مؤقتًا',
    pause_hint: 'أثناء الازدحام أو عطل: لا يمنح رمز QR أي رقم، وتطلب صفحة الزبون الانتباه للرقم. تواصل شاشة الفريق وشاشة التلفاز العمل.',
    paused_tag: 'التسجيل متوقف مؤقتًا',
    active_tag: 'التسجيل مفعّل',
    feedback_section: 'آراء الزبائن',
    place_id_label: 'Google Place ID',
    place_id_hint: 'الصق معرّف صفحتك على Google أو رابط «كتابة تقييم». يُستخدم لإرسال رابط التقييم بعد الاستلام.',
    review_link_preview: 'رابط التقييم المرسل إلى الزبائن',
    feedback_label: 'إرسال سؤال الرضا بعد الاستلام',
    feedback_hint: 'سؤال واحد بثلاثة أزرار على واتساب، بعد نحو 15 دقيقة من الاستلام، مرة واحدة في الشهر على الأكثر لكل زبون.',
    google_link_label: 'اقتراح رابط التقييم على Google',
    google_link_hint: 'بعد الإجابة، يُرسل رابط «كتابة تقييم» المباشر إلى الجميع دون تصفية. يتطلب Google Place ID.',
    google_link_needs_id: 'لا يوجد Google Place ID: لن يُرسل رابط التقييم.',
    on: 'مفعّل',
    off: 'معطّل',
    add_range: 'إضافة فترة عمل',
    remove_range: 'إزالة هذه الفترة',
    today_hours: 'أوقات العمل اليوم:',
    closed_today: 'مغلق اليوم.',
    bad_mode: 'وضع ترقيم غير صالح.',
    bad_default_lang: 'يجب أن تكون اللغة الافتراضية من بين اللغات المختارة.',
    bad_place_id: 'Google Place ID غير صالح. الصق المعرّف أو رابط «كتابة تقييم» لصفحتك على Google.',
    bad_setting: 'قيمة غير صالحة.',
    request_failed: 'حدث خطأ. أعد المحاولة.',
    error_unauthorized: 'يلزم تسجيل الدخول.',
    error_not_found: 'غير موجود.',
    error_bad_request: 'طلب غير صالح.',
    error_not_waiting: 'لم يعد الطلب قيد التحضير.',
    error_not_open: 'الطلب مغلق بالفعل.',
    error_not_ready: 'الطلب ليس جاهزًا.',
    error_paused: 'التسجيل متوقف مؤقتًا.',
    error_resend_cooldown: 'انتظر دقيقة قبل إعادة الإرسال.',
    error_not_callable: 'لا يمكن الاتصال بهذا العميل عبر واتساب: يجب أن يكون الطلب جاهزًا ومسجلًا عبر واتساب.',
    error_call_in_progress: 'هناك اتصال واتساب جارٍ بالفعل لهذا الطلب.',
    error_demo_login: 'اسم المستخدم أو كلمة المرور غير صحيحة.',
    error_wrong_code: 'الرمز غير صحيح.',
    error_locked: 'محاولات كثيرة. أعد المحاولة لاحقًا.',
    error_code_cooldown: 'تم إرسال رمز للتو. انتظر قليلًا.',
    error_bad_role: 'دور غير صالح.',
    error_already_member: 'هذا الشخص لديه هذا الدور بالفعل.',
    error_last_owner: 'لا يمكن إزالة المالك الأخير.',
    error_bad_mode: 'وضع ترقيم غير صالح.',
    error_bad_name: 'اسم غير صالح (80 حرفًا على الأكثر).',
    error_bad_lang: 'لغة غير صالحة.',
    error_no_free_number: 'لم يتبقَّ أي رقم لهذا اليوم.',
    error_session_expired: 'انتهت الجلسة. أدخل رمز المنضدة.',
    error_message_too_long: 'الرسالة طويلة جدًا.',
    error_message_placeholder: 'أحد متغيرات الرسالة غير موجود أو مكتوب بشكل خاطئ.',
    error_logo_too_large: 'الملف كبير جدًا بالنسبة إلى شعار.',
    error_logo_dimensions: 'الصورة كبيرة جدًا بالنسبة إلى شعار.',
    logo_section: 'الشعار',
    logo_intro: 'يظهر في صفحة الزبون بجانب اسم المؤسسة، وفي أعلى شاشة التلفاز بدل شعار RingRing.',
    logo_hint: 'ملف PNG أو SVG، بحجم أقصاه {{max}} كيلوبايت.',
    logo_current: 'الشعار الحالي',
    logo_none: 'لا يوجد شعار: يظهر اسم المؤسسة وحده.',
    logo_choose: 'اختيار شعار',
    logo_replace: 'استبدال الشعار',
    logo_remove: 'إزالة الشعار',
    logo_uploading: 'جارٍ رفع الشعار…',
    logo_saved: 'تم حفظ الشعار',
    logo_removed: 'تمت إزالة الشعار',
    logo_not_saved: 'تعذّر حفظ الشعار. حاول مرة أخرى.',
    logo_empty: 'هذا الملف فارغ.',
    logo_too_large: 'الملف كبير جدًا: الحد الأقصى {{max}} كيلوبايت.',
    logo_bad_type: 'صيغة غير مقبولة: اختر ملف PNG أو SVG.',
    logo_dimensions: 'الصورة كبيرة جدًا: الحد الأقصى {{px}} × {{px}} بكسل.',
    logo_unsafe: 'يحتوي ملف SVG هذا على برنامج نصي أو رابط خارجي. صدّر ملف SVG بسيطًا أو ملف PNG.',
    messages_section: 'الرسائل إلى الزبائن',
    messages_intro: 'أعد كتابة رسالة التسجيل ورسالة «الطلب جاهز» بكل لغة. يتلقى كل زبون الرسالة باللغة التي اختارها، وتحتفظ كل لغة لا تعدّلها بالنص الافتراضي.',
    messages_count_hint: 'الحد الأقصى {{max}} حرفًا لكل رسالة، تُحسب على المعاينة: مع اسم المؤسسة وأطول اسم لنقطة الاستلام والسطر الأخير.',
    message_lang: 'لغة الرسالة',
    message_registered: 'رسالة التسجيل',
    message_registered_hint: 'تُرسل على واتساب عندما يفعّل الزبون التنبيه.',
    message_ready: 'رسالة «الطلب جاهز»',
    message_ready_hint: 'تُرسل على واتساب عندما يحدّد الفريق الطلب على أنه جاهز.',
    message_closing: 'السطر الأخير، يُضاف تلقائيًا:',
    message_counter: '{{count}} / {{max}} حرفًا',
    message_over: 'أطول من الحد بـ {{count}} حرفًا',
    message_required: 'المتغيرات الإلزامية:',
    message_insert: 'إدراج متغير',
    placeholder_order_number: 'رقم الطلب',
    placeholder_venue_name: 'اسم المؤسسة',
    placeholder_pickup_point: 'نقطة الاستلام',
    message_preview: 'معاينة واتساب',
    message_sms_preview: 'رسالة SMS احتياطية، إذا لم تصل رسالة واتساب خلال 20 ثانية',
    message_sms_default: 'هذا النص لا يتسع في رسالة SMS واحدة: تُرسل رسالة SMS القصيرة الافتراضية.',
    message_restore: 'استعادة النص الافتراضي',
    message_default_tag: 'النص الافتراضي',
    message_custom_tag: 'نص مخصص',
    message_problem: '{{lang}}، {{message}}: {{problem}}',
    message_too_long: 'الرسالة طويلة جدًا: الحد الأقصى {{max}} حرفًا، مع السطر الأخير.',
    message_missing_placeholder: 'المتغير {{placeholder}} غير موجود.',
    message_bad_placeholder: 'متغير غير معروف أو مكتوب بشكل خاطئ: {{placeholder}}.',
    message_link: 'لا تُقبل الروابط في هذه الرسائل.',
    poster_section: 'ملصق المنضدة',
    poster_intro: 'اطبع الملصق بحجم A4 أو A5 وضعه قرب الصندوق: يفتح رمز QR صفحة هذه المنضدة.',
    poster_lang_2: 'اللغة الثانية للملصق',
    poster_lang_hint: 'يُطبع الملصق باللغة {{lang}} (اللغة الافتراضية) وباللغة الثانية التي تختارها.',
    poster_one_lang: 'أضف لغة ثانية إلى الصفحة للحصول على ملصق بلغتين.',
    poster_download: 'تنزيل ملصق {{size}}',
    poster_downloading: 'جارٍ تجهيز الملصق…',
    poster_downloaded: 'تم تنزيل الملصق',
    poster_failed: 'تعذّر تجهيز الملصق. حاول مرة أخرى.',
    poster_save_first: 'يعتمد الملصق على الإعدادات المحفوظة: احفظ تغييراتك قبل تنزيله.',
    bad_poster_lang: 'اختر اللغة الثانية للملصق من بين لغات الصفحة، غير اللغة الافتراضية.',
    error_bad_poster_size: 'مقاس ملصق غير صالح: A4 أو A5.',
    error_pickup_point_archived: 'هذه المنضدة مؤرشفة: لا تستقبل الطلبات وليس لها ملصق.',
  },
};

/**
 * The copy key of each API error code. The API puts the label in `error.label` in the request language
 * (`Accept-Language`, then the venue or user language); the web app computes it again from `error.code` in the
 * screen language, so a language switch relabels an error on screen. `error.message` stays machine-readable text for
 * API clients and logs; it is never shown. Unknown codes get the generic line, so a raw code never reaches a screen.
 */
export const API_ERROR_KEYS: Readonly<Record<string, keyof UiCopy>> = {
  unauthorized: 'error_unauthorized',
  not_found: 'error_not_found',
  bad_request: 'error_bad_request',
  bad_number: 'bad_number',
  number_open: 'number_open',
  device_limit: 'too_many_open',
  not_waiting: 'error_not_waiting',
  not_open: 'error_not_open',
  not_ready: 'error_not_ready',
  paused: 'error_paused',
  resend_limit: 'resend_exhausted',
  resend_cooldown: 'error_resend_cooldown',
  not_callable: 'error_not_callable',
  call_in_progress: 'error_call_in_progress',
  demo_login_failed: 'error_demo_login',
  resend_not_ready: 'error_not_ready',
  no_free_number: 'error_no_free_number',
  wrong_pin: 'error_wrong_code',
  pin_locked: 'error_locked',
  device_revoked: 'device_revoked_message',
  board_link_required: 'tv_link_missing_title',
  board_link_invalid: 'tv_link_invalid_title',
  session_expired: 'error_session_expired',
  forbidden: 'no_access',
  bad_phone: 'bad_phone',
  wrong_code: 'error_wrong_code',
  code_locked: 'error_locked',
  code_expired: 'code_expired',
  code_cooldown: 'error_code_cooldown',
  code_not_sent: 'code_not_sent',
  channel_unavailable: 'code_not_sent',
  no_backup_email: 'no_backup_email',
  bad_email: 'bad_email',
  bad_role: 'error_bad_role',
  already_member: 'error_already_member',
  last_owner: 'error_last_owner',
  bad_org_name: 'bad_org_name',
  bad_venue_name: 'bad_venue_name',
  bad_city: 'bad_city',
  bad_hours: 'bad_hours',
  bad_mode: 'error_bad_mode',
  bad_languages: 'bad_languages',
  bad_name: 'error_bad_name',
  last_pickup_point: 'last_pickup_point',
  bad_lang: 'error_bad_lang',
  bad_default_lang: 'bad_default_lang',
  bad_place_id: 'bad_place_id',
  bad_setting: 'bad_setting',
  // issue #20: the settings page words these itself with the limit or the variable; the label stays general
  message_too_long: 'error_message_too_long',
  message_missing_placeholder: 'error_message_placeholder',
  message_bad_placeholder: 'error_message_placeholder',
  message_link: 'message_link',
  logo_empty: 'logo_empty',
  logo_too_large: 'error_logo_too_large',
  logo_bad_type: 'logo_bad_type',
  logo_dimensions: 'error_logo_dimensions',
  logo_unsafe: 'logo_unsafe',
  logo_not_saved: 'logo_not_saved',
  // the counter poster (issue #13)
  bad_poster_lang: 'bad_poster_lang',
  bad_size: 'error_bad_poster_size',
  pickup_point_archived: 'error_pickup_point_archived',
};

/** Every error code that has a label: the API answers these with a 4xx and the label; anything else is a 500. */
export const API_ERROR_CODES: readonly string[] = Object.keys(API_ERROR_KEYS);

export function apiErrorLabel(lang: Lang, code: string | null | undefined): string {
  const key = code && Object.prototype.hasOwnProperty.call(API_ERROR_KEYS, code) ? API_ERROR_KEYS[code] : undefined;
  return key ? UI[lang][key] : UI[lang].request_failed;
}

/** A template cut at its `{{placeholders}}`, so a screen can wrap a variable (a number, a phone) in its own LTR run. */
export type TemplatePart = { kind: 'text'; text: string } | { kind: 'var'; name: string; text: string };

export function fillParts(template: string, vars: Record<string, string>): TemplatePart[] {
  const parts: TemplatePart[] = [];
  const re = /\{\{(\w+)\}\}/g;
  let last = 0;
  for (let m = re.exec(template); m !== null; m = re.exec(template)) {
    if (m.index > last) parts.push({ kind: 'text', text: template.slice(last, m.index) });
    parts.push({ kind: 'var', name: m[1], text: vars[m[1]] ?? '' });
    last = m.index + m[0].length;
  }
  if (last < template.length) parts.push({ kind: 'text', text: template.slice(last) });
  return parts;
}

export function fill(template: string, vars: Record<string, string>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, k: string) => vars[k] ?? '');
}

/** What the customer page shows for each order state: a symbol (never color alone), a label and one body line. */
export interface StatusCopy {
  symbol: string;
  label: string;
  body: string;
}

const STATUS_KEYS: Record<TicketStatus, { symbol: string; label: keyof UiCopy; body: keyof UiCopy | null }> = {
  waiting: { symbol: '⏱', label: 'order_preparing', body: null },
  ready: { symbol: '✓', label: 'order_ready', body: 'ready_body' },
  collected: { symbol: '✓', label: 'order_collected', body: 'collected_body' },
  cancelled: { symbol: '✕', label: 'order_cancelled', body: 'cancelled_body' },
  expired: { symbol: '⏱', label: 'order_expired', body: 'expired_body' },
  abandoned: { symbol: '✕', label: 'order_abandoned', body: 'abandoned_body' },
};

export function customerStatusCopy(lang: Lang, status: TicketStatus): StatusCopy {
  const k = STATUS_KEYS[status];
  const ui = UI[lang];
  return { symbol: k.symbol, label: ui[k.label], body: k.body ? ui[k.body] : '' };
}

/** All six states at once, for the page script that switches state live without a reload. */
export function customerStatusTable(lang: Lang): Record<TicketStatus, StatusCopy> {
  const out = {} as Record<TicketStatus, StatusCopy>;
  for (const s of Object.keys(STATUS_KEYS) as TicketStatus[]) out[s] = customerStatusCopy(lang, s);
  return out;
}

const CHANNEL_KEYS: Record<Channel, { symbol: string; label: keyof UiCopy }> = {
  whatsapp: { symbol: '💬', label: 'channel_whatsapp' },
  web: { symbol: '🌐', label: 'channel_web' },
  sms: { symbol: '✉', label: 'channel_sms' },
  push: { symbol: '🔔', label: 'channel_push' },
  none: { symbol: '—', label: 'channel_none' },
};

/** Channel badge for staff screens: a symbol plus a translated label, never the phone number. */
export function channelCopy(lang: Lang, channel: string): { symbol: string; label: string } {
  const k = CHANNEL_KEYS[(channel in CHANNEL_KEYS ? channel : 'none') as Channel];
  return { symbol: k.symbol, label: UI[lang][k.label] };
}

const DELIVERY_KEYS: Record<DeliveryState, { symbol: string; label: keyof UiCopy }> = {
  queued: { symbol: '⏳', label: 'message_queued' },
  sending: { symbol: '⏳', label: 'message_sending' },
  sent: { symbol: '➤', label: 'message_sent' },
  delivered: { symbol: '✓', label: 'message_delivered' },
  read: { symbol: '✓', label: 'message_delivered' },
  failed: { symbol: '✕', label: 'message_failed' },
};

/**
 * Delivery badge for staff screens: the message state, separate from the order state. "read" shows as delivered
 * (the copy library has no read label and the guest's reading habits are not the staff's business).
 */
export function deliveryCopy(lang: Lang, state: DeliveryState): { symbol: string; label: string } {
  const k = DELIVERY_KEYS[state] ?? DELIVERY_KEYS.queued;
  return { symbol: k.symbol, label: UI[lang][k.label] };
}
